import React from "react";
import { createReactMount } from "@/main/react/mountContract";
import { Localizer } from "@/main/react/Types";
import { DiscussionContentDisplayer, BaseProps as DiscussionContentDisplayerProps } from "@/main/react/discussion/DiscussionContentDisplayer";
import { parsePassageAnchorState, type PassageAnchoredThoughtSummary } from "@/main/react/discussion/passage/mapBlocksToDom";
import { FollowAuthorPrompt } from "@/main/react/discussion/FollowAuthorPrompt";
import DiscussionThoughtsDisplayer, { BaseProps as DiscussionThoughtsDisplayerProps}
    from "@/main/react/discussion/DiscussionThoughtsDisplayer";
import { MetaHandler } from "@/main/common/MetaHandler";
import { AdSlot } from "@/main/react/ads/AdSlot";
import ConversationDisplayer, { BaseProps as ConversationDisplayerProps}
    from "@/main/react/discussion/ConversationDisplayer";
import { MAIN_PAGE_LIST_WIDTH_CLASS, MainPageListStack } from "@/main/react/list/MainPageListLayout";
import {PaginatedListWrapperV2} from "@/main/react/list/PaginatedListWrapperV2";
import {
    DiscussionCardListSkeleton,
    ProfileCardListSkeleton,
    SeriesCardListSkeleton,
} from "@/main/react/list/PaginatedListSkeletons";
import {createStandardPaginatedStatusSlots, PaginatedListEmptyState} from "@/main/react/list/PaginatedListState";
import {ClientFactory} from "@/main/common/ClientFactory";
import {DiscussionWithoutContentDTO, GroupDTO, RelatedDiscussionDTO, SeriesDTO, UserProfileDTO} from "@/generated-client";
import { GroupCardDisplayer } from "@/main/react/card/GroupCardDisplayer";
import {
    onSeriesAddedToDiscussion,
    onSeriesRemovedFromDiscussion,
    onGroupAddedToDiscussion,
    onGroupRemovedFromDiscussion,
    onDiscussionViewerAgreementChanged,
    onDiscussionAddToGroupRequestResolved
} from "@/main/common/EventRegistry";
import {UserCardDisplayer} from "@/main/react/card/UserCardDisplayer";
import {SeriesCardDisplayer} from "@/main/react/card/SeriesCardDisplayer";
import {DiscussionCardDisplayer} from "@/main/react/card/DiscussionCardDisplayer";
import DiscussionRequestsList from "@/main/react/discussion/DiscussionRequestsList";
import DiscussionGroupSummary, { DiscussionGroupSummarySkeleton, mergeDiscussionGroups } from "@/main/react/discussion/DiscussionGroupSummary";
import DiscussionSeriesSummary from "@/main/react/discussion/DiscussionSeriesSummary";
import DiscussionProvenanceSummary from "@/main/react/discussion/DiscussionProvenanceSummary";
import DiscussionRail from "@/main/react/discussion/DiscussionRail";
import DiscussionPageSectionControls, {
    buildDiscussionSectionDefs,
    resolveRepliesTabLabel,
} from "@/main/react/discussion/DiscussionPageSectionControls";
import DiscussionPageLoadFailed from "@/main/react/discussion/DiscussionPageLoadFailed";
import DiscussionPageSkeleton from "@/main/react/discussion/DiscussionPageSkeleton";
import {showAddDiscussionToGroupsDialog} from "@/main/react/Dialog/AddDiscussionToGroupDialog";
import {showAddDiscussionToSeriesDialog} from "@/main/react/Dialog/AddDiscussionToSeriesDialog";
import {Compass, ListPlus, Lock, MessagesSquare, Quote, Share2, Users} from "lucide-react";
import { isBrowserOwnedClick } from "@/main/navigation/NavigationPolicy";
import SensitivePageGate from "@/main/react/sensitive/SensitivePageGate";
import type { SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import { useSensitivePageUnlock } from "@/main/react/sensitive/useSensitivePageUnlock";
import logger from "@/lib/logger";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { SsrSlotFallback } from "@/main/react/list/ssrFallbacks";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import { parseInlinedBoardView, type BoardView } from "@/main/react/board/boardView";
import { normalizePollView, parseInlinedPollView } from "@/main/react/poll/pollView";
import type { PollView } from "@/main/common/PollTypes";
import { popOwnInPageEntries, replaceInPageHistoryUrl } from "@/main/navigation/NavigationHistoryState";
import { readShareMomentKind, withoutShareMomentParam } from "@/main/share/shareMomentParam";
import { openShareMoment } from "@/main/react/share/openShareMoment";
import { DISCUSSION_PUBLISHED_MOMENT, onReviewMoment } from "@/main/mobile-shell/MobileAppReviewBridge";
import { useHandoffCandidate } from "@/main/react/handoff/useHandoffCandidate";
import { HANDOFF_FROM_PARAM, HANDOFF_FROM_VALUE, HandoffCard } from "@/main/react/handoff/HandoffCard";
import type { PublishThoughtSurface } from "@/main/common/ThoughtPublishTypes";
import { PublishedAcknowledgement } from "@/main/react/discussion/PublishedAcknowledgement";
import {useHistoryBackedState} from "@/main/react/page/useHistoryBackedState";
import {normalizeDisplayUsername} from "@/main/common/UsernameDisplayUtils";
import {
    areNormalizedStringArraysEqual,
    DiscussionOpeningThoughtOrderingStyle,
    isRecognizedDiscussionOpeningThoughtOrdering,
    normalizeDiscussionOpeningThoughtOrdering,
    normalizeDiscussionGroupPublicIds,
    OpeningThoughtWithGroupMatchesDTO,
} from "@/main/react/discussion/OpeningThoughtGroupTypes";
import {parseQuestionState, type QuestionState, type QuestionStateView} from "@/main/common/QuestionFacetTypes";
import {InfoBanner} from "@/main/react/component/InfoBanner";
import {Button} from "@/components/ui/button";
import { getPageContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import { filterBookmarksSeries, isBookmarksSeries } from "@/main/react/series/BookmarksSeriesLocalization";
import { normalizeSeriesReadingContext, type SeriesReadingContext } from "@/main/react/series/SeriesReadingTypes";
import { isUnresolvedSegment } from "@/main/navigation/UnresolvedPathSegment";
import { useFocusedThoughtViewBeacon } from "@/main/discussion/ThoughtViewTracker";
import { shareTitleFromHtml } from "@/main/react/discussion/shareTitleFromHtml";

function getHighlightedArgumentPublicIdFromHash(hash?: string): string | undefined {
    const sourceHash = hash ?? (typeof window !== "undefined" ? window.location.hash : "");
    if (!sourceHash) {
        return undefined;
    }

    const match = sourceHash.match(/^#argument-(.+)$/);
    if (!match?.[1]) {
        return undefined;
    }

    try {
        return decodeURIComponent(match[1]).trim() || undefined;
    } catch {
        return match[1].trim() || undefined;
    }
}

type DiscussionRouteState = {
    section?: string;
    conversationPublicId?: string;
    highlightedArgumentPublicId?: string;
    selectedGroupPublicIds?: string[];
    originGroupPublicId?: string;
    originSeriesPublicId?: string;
    openingArgumentOrdering?: DiscussionOpeningThoughtOrderingStyle;
};

function parseSelectedGroupPublicIdsFromSearch(search?: string): string[] {
    if (!search) {
        return [];
    }

    const value = new URLSearchParams(search).get("groupPublicIds");
    if (!value) {
        return [];
    }

    return normalizeDiscussionGroupPublicIds(value.split(","));
}

function parseOriginGroupPublicIdFromSearch(search?: string): string | undefined {
    if (!search) {
        return undefined;
    }

    return trimToUndefined(new URLSearchParams(search).get("fromGroupPublicId"));
}

function parseOriginSeriesPublicIdFromSearch(search?: string): string | undefined {
    if (!search) {
        return undefined;
    }

    return trimToUndefined(new URLSearchParams(search).get("fromSeriesPublicId"));
}

function parseOpeningArgumentOrderingFromSearch(search?: string): DiscussionOpeningThoughtOrderingStyle {
    if (!search) {
        return "best";
    }

    return normalizeDiscussionOpeningThoughtOrdering(new URLSearchParams(search).get("openingArgumentOrdering"));
}

/**
 * Normalizes a route/query fragment to `undefined` when it carries no usable value.
 *
 * "No usable value" includes the literal strings `"null"` and `"undefined"`, not just empty and
 * whitespace — and that is the half this used to miss. It feeds the section, the highlighted thought
 * id, and the origin group/series ids, none of which can legitimately be the four characters `null`.
 *
 * Missing it made a bad URL self-sustaining: `/discussion/{slug}/null` parsed to a section of
 * `"null"` (non-empty, so it survived), which `buildDiscussionPageUrl` then re-emitted verbatim on
 * the next URL build (its `state.section !== ""` test passes for `"null"`). One bad visit therefore
 * persisted for the rest of the session and got bookmarked and shared from there. Same mechanism
 * produced `/discussion/{slug}/thought/null` through `highlightedArgumentPublicId`.
 *
 * The navigation seams now refuse to REQUEST such a URL (`UnresolvedPathSegment`); this is the other
 * half — when one arrives anyway, from a bookmark or an external link, it degrades to the default
 * section instead of being adopted as state.
 */
function trimToUndefined(value?: string | null) {
    return isUnresolvedSegment(value) ? undefined : value?.trim();
}

function normalizeUsernameToUndefined(value?: string | null) {
    return normalizeDisplayUsername(value) ?? undefined;
}

function localize(localizer: Localizer, key: LocalizationKey, ...args: string[]): string {
    return localizer(key, ...args);
}

function getVisibleDiscussionSeriesCount(totalCount: number, savedToBookmarks: boolean) {
    return Math.max(0, Number(totalCount ?? 0) - (savedToBookmarks ? 1 : 0));
}

function readDiscussionRouteState(
    location: { pathname: string; hash: string; search: string },
    fallback: DiscussionRouteState,
    discussionSlug?: string,
): DiscussionRouteState {
    const selectedGroupPublicIds = parseSelectedGroupPublicIdsFromSearch(location.search);
    const originGroupPublicId = parseOriginGroupPublicIdFromSearch(location.search);
    const originSeriesPublicId = parseOriginSeriesPublicIdFromSearch(location.search);
    const openingArgumentOrdering = parseOpeningArgumentOrderingFromSearch(location.search);

    if (!discussionSlug) {
        return {
            ...fallback,
            selectedGroupPublicIds,
            originGroupPublicId,
            originSeriesPublicId,
            openingArgumentOrdering,
        };
    }

    const segments = location.pathname
        .split("/")
        .filter(Boolean)
        .map((segment) => {
            try {
                return decodeURIComponent(segment);
            } catch {
                return segment;
            }
        });

    if (segments[0] !== "discussion" || segments[1] !== discussionSlug) {
        return {
            ...fallback,
            selectedGroupPublicIds,
            originGroupPublicId,
            originSeriesPublicId,
            openingArgumentOrdering,
        };
    }

    if (!segments[2]) {
        return {
            section: "conversations",
            conversationPublicId: undefined,
            highlightedArgumentPublicId: undefined,
            selectedGroupPublicIds,
            originGroupPublicId,
            originSeriesPublicId,
            openingArgumentOrdering,
        };
    }

    if (segments[2] === "conversation" && segments[3]) {
        return {
            section: "conversation",
            conversationPublicId: segments[3],
            highlightedArgumentPublicId: trimToUndefined(
                segments[4] === "thought" || segments[4] === "argument" ? segments[5] : undefined,
            ) ?? getHighlightedArgumentPublicIdFromHash(location.hash),
            selectedGroupPublicIds,
            originGroupPublicId,
            originSeriesPublicId,
            openingArgumentOrdering,
        };
    }

    if ((segments[2] === "thought" || segments[2] === "argument") && segments[3]) {
        return {
            section: "opening",
            conversationPublicId: undefined,
            highlightedArgumentPublicId: undefined,
            selectedGroupPublicIds,
            originGroupPublicId,
            originSeriesPublicId,
            openingArgumentOrdering,
        };
    }

    return {
        section: trimToUndefined(segments[2]) ?? fallback.section,
        conversationPublicId: undefined,
        highlightedArgumentPublicId: undefined,
        selectedGroupPublicIds,
        originGroupPublicId,
        originSeriesPublicId,
        openingArgumentOrdering,
    };
}

function buildDiscussionPageUrl(
    state: DiscussionRouteState,
    location: { pathname: string; search: string; hash: string },
    discussionSlug?: string,
    openingArgumentPublicId?: string,
) {
    if (!discussionSlug) {
        return `${location.pathname}${location.search}${location.hash}`;
    }

    const base = `/discussion/${discussionSlug}`;
    let nextPath = base;

    if (state.section === "conversation" && state.conversationPublicId) {
        nextPath = state.highlightedArgumentPublicId
            ? `${base}/conversation/${state.conversationPublicId}/thought/${state.highlightedArgumentPublicId}`
            : `${base}/conversation/${state.conversationPublicId}`;
    } else if (state.section === "opening" && openingArgumentPublicId) {
        nextPath = `${base}/thought/${openingArgumentPublicId}`;
    } else if (state.section && state.section !== "") {
        nextPath = `${base}/${state.section}`;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    const normalizedSelectedGroupPublicIds = normalizeDiscussionGroupPublicIds(state.selectedGroupPublicIds);
    const normalizedOriginGroupPublicId = trimToUndefined(state.originGroupPublicId);
    const normalizedOriginSeriesPublicId = trimToUndefined(state.originSeriesPublicId);
    const normalizedOpeningArgumentOrdering = normalizeDiscussionOpeningThoughtOrdering(state.openingArgumentOrdering);

    if (normalizedOriginGroupPublicId) {
        nextSearchParams.set("fromGroupPublicId", normalizedOriginGroupPublicId);
    } else {
        nextSearchParams.delete("fromGroupPublicId");
    }

    if (normalizedSelectedGroupPublicIds.length > 0) {
        nextSearchParams.set("groupPublicIds", normalizedSelectedGroupPublicIds.join(","));
    } else {
        nextSearchParams.delete("groupPublicIds");
    }

    if (normalizedOriginSeriesPublicId) {
        nextSearchParams.set("fromSeriesPublicId", normalizedOriginSeriesPublicId);
    } else {
        nextSearchParams.delete("fromSeriesPublicId");
    }

    if (normalizedOpeningArgumentOrdering === "best") {
        nextSearchParams.delete("openingArgumentOrdering");
    } else {
        nextSearchParams.set("openingArgumentOrdering", normalizedOpeningArgumentOrdering);
    }

    const nextSearch = nextSearchParams.toString();
    return `${nextPath}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`;
}

/**
 * The BOARD facet's island. `lazyWithRetry`, not a MOUNT_MAP entry — see the render site below and
 * design §11.1. The chunk key is stable and kebab-case (rule 26), and this import is what keeps the
 * grid off the eager startup graph (rule 27).
 */
const BoardGrid = lazyWithRetry(() => import("@/main/react/board/BoardGrid"), "board-grid");

/**
 * The POLL facet's island, on the board's exact terms (poll design §11.3.3): `lazyWithRetry`, not a
 * `MOUNT_MAP` entry, rendered into the content card's `pollSlot` directly after the board's.
 */
const PollBlock = lazyWithRetry(() => import("@/main/react/poll/PollBlock"), "poll-block");

/**
 * The short-post focal view (round 31 `2c` remainder): the post a short post responds to, ABOVE the
 * body, and its replies with the count and the reply box BELOW it. One chunk, two exports, so one
 * `chunkKey`. Off the eager graph like the board and the poll; mounted only on a short post's page.
 */
const FocalPostParentLine = lazyWithRetry(
    () => import("@/main/react/shortpost/FocalPostNavigator").then((module) => ({ default: module.FocalPostParentLine })),
    "short-post-focal",
);
const FocalPostNavigator = lazyWithRetry(
    () => import("@/main/react/shortpost/FocalPostNavigator").then((module) => ({ default: module.FocalPostNavigator })),
    "short-post-focal",
);
// The server-rendered facet blocks (`board_grid.jte`, `poll_block.jte`), addressed by their own hooks
// inside this island's SSR snapshot; the Suspense fallbacks below put them back while a chunk is pending.
const BOARD_SSR_SELECTOR = '[data-playwright-id="discussion-board-ssr"]';
const POLL_SSR_SELECTOR = '[data-playwright-id="discussion-poll-ssr"]';

/**
 * The sections whose URL is a DESTINATION rather than a view of the article (`DISCUSSION-10`).
 *
 * <p>Deliberately excludes the reading routes (`conversations`, `opening`, `conversation`): on those
 * the article IS what the visitor asked for, so the top of the page is the right landing point. Used
 * only to decide whether a cold arrival scrolls; it is not a route table and nothing routes off it.
 */
export const DEEP_LINKED_DISCUSSION_SECTIONS: ReadonlySet<string> = new Set([
    "participants",
    "related",
    // Quote post (design §10.3). Deep-linkable for the same reason the others are: the "X quoted your
    // post" notification lands the author on the QUOTING post, and from there the natural next move is
    // their own post's list of who quoted it.
    "quoted-by",
    "groups",
    "series",
    "requests",
]);

type DiscussionPageProps = {
    localizer: Localizer;
    section?: string;
    openingUserPresent?: boolean;
    reactingUserPresent?: boolean;
    highlightedArgumentPublicId?: string;
    deferInitialLoad?: boolean;
    spoiler?: boolean;
    /**
     * Tier-2 finding 23 — the post's topics, mirrored from the crawlable JTE block that the island's
     * own `createRoot()` discards on mount.
     */
    tagNames?: string[];
    seriesReadingContext?: SeriesReadingContext;
    /**
     * The discussion's top-level thoughts, inlined by the server into the page it already rendered
     * (`discussion.jte`'s `script[data-discussion-thoughts]`). Seeds the thought list's first paint and
     * keeps it populated if `/ajax/thoughts/discussion` fails. Absent on the deferred soft-nav shell
     * and on a degraded server render — both of which correctly fall back to fetching.
     */
    inlinedThoughts?: OpeningThoughtWithGroupMatchesDTO[];
    /**
     * The ordering the inlined rows above are in, as the SERVER declared it on the payload element
     * (`discussion.jte`'s `data-thoughts-ordering`). Undefined on a soft-nav shell, on a degraded
     * render, and on a page from a template older than 2026-09-08 — all of which fall back to
     * `SSR_SEEDED_THOUGHTS_ORDERING`.
     *
     * <p>It exists because the seed's CURSOR shape follows its ordering, and the client used to decide
     * that from a constant mirrored across the seam with a "keep the two in sync" comment on it. The
     * attribute has been emitted since the handoff shipped and read by nothing (register F20).
     */
    inlinedThoughtsOrdering?: DiscussionOpeningThoughtOrderingStyle;
    /**
     * QUESTION facet. Present on a direct load from the mount container's `data-*` attributes and on a
     * soft navigation from `/ajax/discussion/page-data/*`. `questionState` is the SERVER's derivation
     * (`OPEN` | `ANSWERED` | `RESOLVED`) and must never be recomputed here from `acceptedThoughtPublicId`
     * — that pointer outlives a soft delete, so the two disagree exactly when it matters (Q-D10).
     */
    isQuestion?: boolean;
    questionState?: QuestionState;
    acceptedThoughtPublicId?: string;
    /**
     * SV-130 "Ask someone to answer" eligibility (round-29 D12, A19): viewer-specific, false for a
     * non-author and for an asker in the holdout. `data-ask-to-answer-available` on a direct load,
     * `askToAnswerAvailable` on /page-data; fail-CLOSED on both (absent reads as false).
     */
    askToAnswerAvailable?: boolean;
    /**
     * CHANGED MY MIND: has anything on this post ever changed its author's mind?
     *
     * One bit, exactly like the board bit. WHICH thoughts are marked travels on each thought's own
     * `mindChanged` flag, so a second per-post list here would be a duplicate that can disagree.
     */
    mindChanged?: boolean;
    /**
     * Round-33 `2b` (r33 D13): the post's page name, the same text as its `<title>` (for a SHORT post, its
     * excerpt cut to ~60 characters). Computed server-side only (`SeoService.discussionPageName`), so it
     * travels on both transports: `data-page-name` on a direct load, `pageName` on /page-data. The card
     * renders it visually hidden inside the `<h1>` of a title-less short post, exactly as `discussion.jte`
     * does, so the heading has a name and does not change on hydration (rule 32). Absent for a deleted post.
     */
    pageName?: string;
    /**
     * PASSAGE anchors block-id list, the direct-load copy (BL-160): `discussion.jte` emits it as a JSON
     * array in `data-passage-block-ids`, read here so the island never fetches `/page-data` for it. An
     * array (possibly empty) is the server's answer; `undefined` is an older server render, which keeps
     * `usePassageBlockIds`' client fetch.
     */
    passageBlockIds?: Array<string | null>;
    /**
     * REVIEW facet. Same three sources as the question fields above, and the same reason all three
     * travel together: the header renders only when it has a name AND a rating, so carrying one
     * without the other would produce a half-drawn strip rather than none.
     */
    reviewSubjectName?: string;
    reviewSubjectSlug?: string;
    reviewRatingHalfStars?: number;
    /**
     * BOARD facet. Arrives as the whole board rather than as flags, because a grid, a queue and a
     * settled record do not fit data attributes — the server inlines it as JSON beside the grid it
     * rendered, and `getProps` reads it BEFORE `mountReactNode` discards the container's children.
     *
     * Undefined means "the server sent no board": either this is not a board post, or the board's
     * server-side load failed and the render is already noindex. Both cases render no grid, which is
     * why this is deliberately not defaulted to an empty board — an empty board asserts something
     * false (design §11.1; rule 32).
     */
    boardView?: BoardView;
    /**
     * BOARD facet, the one bit — whether the server considers this post a board AT ALL, independent of
     * whether it managed to send the board.
     *
     * <p>`boardView` alone cannot answer that: it is `undefined` both for an ordinary discussion and
     * for a board whose server-side load failed. `discussion.jte` therefore emits `data-is-board`
     * outside every conditional, as an explicit `"true"`/`"false"` string so the reader can tell a
     * `false` from an older server that never sent the attribute.
     *
     * <p>Read at mount and threaded here so the island's board handling can distinguish the two cases;
     * today the only consumer is the degraded-load warning in `getProps`. Whether a missing payload
     * should trigger a FETCH is an open UX decision — board plan §H3.
     */
    isBoard?: boolean;
    /**
     * POLL facet, the same two-part shape as the board: the whole poll as the server inlined it beside
     * `poll_block.jte` (read in `getProps` before `createRoot()` discards it), undefined when the server
     * sent none — not a poll post, or a degraded load that is already noindex. Never defaulted to an
     * empty poll (rule 32).
     */
    pollView?: PollView;
    /** POLL, the one bit, emitted outside every conditional as `data-is-poll` like `data-is-board`. */
    isPoll?: boolean;
    reviewSubjectPublicId?: string;
    /**
     * Does the viewer already follow this post's author? Read off the mount container's
     * `data-initially-following-author` attribute on a direct load; the soft-nav value arrives
     * separately through `DiscussionPageData`. Optional because the attribute defaults to "false" and
     * an absent one must read as "not following" rather than as a crash.
     * designs/01-now/2026-09-06-follow-author-prompt-design.md §4
     */
    initiallyFollowingAuthor?: boolean;
    /**
     * May the follow-author prompt invite this viewer to follow the author at all? The server's
     * moderation gate, read off `data-author-followable-by-prompt` on a direct load.
     *
     * ⚠️ Absent reads as FALSE, not as true. A gate whose default is "allowed" is not a gate, and the
     * cost of the strict direction is a missing recommendation rather than a moderated author promoted
     * by name. designs/01-now/2026-09-06-follow-author-prompt-design.md D3
     */
    authorFollowableByPrompt?: boolean;
    /**
     * Round 37 `2a` (FI-583): the viewer's PREVIOUS visit to this post (ISO instant), read off
     * `data-viewer-last-visited-at` on a direct load; the soft-nav value arrives through `DiscussionPageData`.
     * Absent or empty means anonymous or a first visit, and no divider renders.
     */
    viewerLastVisitedAt?: string;
} & DiscussionSectionCountFields
    & Partial<Omit<DiscussionContentDisplayerProps, "localizer">>
    & Partial<Omit<ConversationDisplayerProps, "localizer">>
    & Partial<Omit<DiscussionThoughtsDisplayerProps, "localizer">>;

type DiscussionPageData = {
    discussionPublicId?: string;
    discussionSlug?: string;
    authorUsername?: string;
    authorProfileImageUrl?: string;
    createdAt?: string;
    updatedAt?: string;
    // Non-empty ONLY when the post's latest version was written after its first reply. Carried on both
    // delivery paths below for the reason every field here is: a value present on a hard load and
    // absent after a soft nav renders correctly on reload and then quietly disappears.
    editedAfterReplyAt?: string;
    /**
     * The author's byline distinction, resolved by the server (distinctions design §6.1): a distinction
     * key, and the group name when the author is that group's current Voice of the month. Carried on BOTH
     * delivery paths below (`data-author-featured-distinction` / `data-author-voice-of-month-group-name`
     * on a hard load, `DiscussionPageDataResponse` on a soft navigation), for the reason every field here is.
     */
    authorFeaturedDistinction?: string;
    authorVoiceOfMonthGroupName?: string;
    /**
     * The post's stored text length for the byline's reading-time token (round-6 `2a`). Carried on both
     * delivery paths like the field above; absent renders no label. ⚠️ Neither path emits it yet:
     * `discussion.jte` has no `data-body-text-length` and `DiscussionPageDataResponse` no field
     * (`2026-09-06-read-surface-completion…` §3, the `1a` note), so this is the client half only.
     */
    bodyTextLength?: number;
    title: string;
    privateAccess: boolean;
    sensitive: boolean;
    spoiler: boolean;
    isArchived: boolean;
    isDeleted: boolean;
    archivedAt?: string;
    deletedAt?: string;
    deletedByModerator?: boolean;
    deletedReasonType?: string;
    deletedMessage?: string;
    agreementsCount: number;
    disagreementsCount: number;
    argumentsCount: number;
    addedToSeriesCount: number;
    // Per-section counts for the section tabs. Optional: populated by the bootstrap response
    // when available; absent values simply render no badge.
    conversationsCount?: number;
    participantsCount?: number;
    quotedByCount?: number;
    relatedCount?: number;
    requestsCount?: number;
    initiallyAgreeing: boolean;
    initiallyDisagreeing: boolean;
    initiallyAddedToAnySeries: boolean;
    initiallySavedToBookmarks: boolean;
    // Does the viewer already follow this post's author? Gates the follow-author prompt. Arrives from
    // the SSR data attribute on a direct load and from /page-data on an in-app soft navigation, like
    // every other viewer flag here.
    // designs/01-now/2026-09-06-follow-author-prompt-design.md
    initiallyFollowingAuthor: boolean;
    // The server's moderation gate on the prompt. Arrives from the SSR data attribute on a direct load
    // and from /page-data on a soft navigation, and is fail-closed in both directions.
    authorFollowableByPrompt: boolean;
    // Round 37 `2a` (FI-583): the viewer's previous visit, for the replies divider. Both delivery paths, like
    // every viewer flag here; undefined for anonymous or a first visit.
    viewerLastVisitedAt?: string;
    contentHtml: string;
    // The post's own language, for the `lang` attribute on the rendered body. Arrives from the SSR
    // data attribute on a direct load and from /page-data on an in-app soft navigation.
    contentLanguageTag?: string;
    seriesReadingContext?: SeriesReadingContext;
    isQuestion?: boolean;
    questionState?: QuestionState;
    acceptedThoughtPublicId?: string;
    /**
     * SV-130 "Ask someone to answer" eligibility (round-29 D12, A19): viewer-specific, false for a
     * non-author and for an asker in the holdout. `data-ask-to-answer-available` on a direct load,
     * `askToAnswerAvailable` on /page-data; fail-CLOSED on both (absent reads as false).
     */
    askToAnswerAvailable?: boolean;
    /**
     * CHANGED MY MIND: has anything on this post ever changed its author's mind?
     *
     * One bit, exactly like the board bit. WHICH thoughts are marked travels on each thought's own
     * `mindChanged` flag, so a second per-post list here would be a duplicate that can disagree.
     */
    mindChanged?: boolean;
    /**
     * Round-33 `2b` (r33 D13): the post's page name, the same text as its `<title>` (for a SHORT post, its
     * excerpt cut to ~60 characters). Computed server-side only (`SeoService.discussionPageName`), so it
     * travels on both transports: `data-page-name` on a direct load, `pageName` on /page-data. The card
     * renders it visually hidden inside the `<h1>` of a title-less short post, exactly as `discussion.jte`
     * does, so the heading has a name and does not change on hydration (rule 32). Absent for a deleted post.
     */
    pageName?: string;
    /**
     * REVIEW facet. Same three sources as the question fields above, and the same reason all three
     * travel together: the header renders only when it has a name AND a rating, so carrying one
     * without the other would produce a half-drawn strip rather than none.
     */
    reviewSubjectName?: string;
    reviewSubjectSlug?: string;
    reviewRatingHalfStars?: number;
    reviewSubjectPublicId?: string;
    /** BOARD, carried on the soft-navigation path. See the normalizer for why it is shape-checked. */
    boardView?: BoardView;
    /** POLL, carried on the soft-navigation path once the page-data response sends it. */
    pollView?: PollView;
    /**
     * PASSAGE anchors (D4): carried on the soft-navigation path only. `undefined` on a direct load, which
     * `DiscussionContentDisplayer` reads as "not carried, fetch if the body is long enough"; an array
     * (possibly empty) is the server's answer and is never fetched again.
     */
    passageBlockIds?: Array<string | null>;
};

function numberOrUndefined(value: unknown): number | undefined {
    if (value == null || value === "") {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

type DiscussionSectionCountFields = {
    conversationsCount?: number;
    participantsCount?: number;
    quotedByCount?: number;
    relatedCount?: number;
    requestsCount?: number;
};

function createDiscussionPageDataFromProps(props: DiscussionPageProps): DiscussionPageData | null {
    const discussionPublicId = trimToUndefined(props.discussionPublicId);
    const discussionSlug = trimToUndefined(props.discussionSlug);
    const deletedAt = trimToUndefined(props.deletedAt);

    if (!discussionPublicId && !discussionSlug) {
        return null;
    }

    return {
        discussionPublicId,
        discussionSlug,
        authorUsername: normalizeUsernameToUndefined(props.authorUsername),
        authorProfileImageUrl: trimToUndefined((props as DiscussionPageProps & { authorProfileImageUrl?: string }).authorProfileImageUrl),
        createdAt: trimToUndefined(props.createdAt),
        updatedAt: trimToUndefined(props.updatedAt),
        editedAfterReplyAt: trimToUndefined(props.editedAfterReplyAt),
        authorFeaturedDistinction: trimToUndefined(props.authorFeaturedDistinction),
        authorVoiceOfMonthGroupName: trimToUndefined(props.authorVoiceOfMonthGroupName),
        bodyTextLength: numberOrUndefined(props.bodyTextLength),
        title: props.title?.trim() ?? "",
        pageName: trimToUndefined(props.pageName),
        privateAccess: Boolean(props.privateAccess),
        sensitive: Boolean(props.sensitive),
        spoiler: Boolean((props as DiscussionPageProps & { spoiler?: boolean }).spoiler),
        isArchived: Boolean(props.isArchived),
        isDeleted: Boolean(props.isDeleted ?? deletedAt),
        archivedAt: trimToUndefined(props.archivedAt),
        deletedAt,
        deletedByModerator: Boolean((props as DiscussionPageProps).deletedByModerator),
        deletedReasonType: trimToUndefined((props as DiscussionPageProps).deletedReasonType),
        deletedMessage: trimToUndefined((props as DiscussionPageProps).deletedMessage),
        agreementsCount: Number(props.agreementsCount ?? 0),
        disagreementsCount: Number(props.disagreementsCount ?? 0),
        argumentsCount: Number((props as DiscussionPageProps & { argumentsCount?: number }).argumentsCount ?? 0),
        conversationsCount: numberOrUndefined((props as DiscussionPageProps & DiscussionSectionCountFields).conversationsCount),
        participantsCount: numberOrUndefined((props as DiscussionPageProps & DiscussionSectionCountFields).participantsCount),
        quotedByCount: numberOrUndefined((props as DiscussionPageProps & DiscussionSectionCountFields).quotedByCount),
        relatedCount: numberOrUndefined((props as DiscussionPageProps & DiscussionSectionCountFields).relatedCount),
        requestsCount: numberOrUndefined((props as DiscussionPageProps & DiscussionSectionCountFields).requestsCount),
        addedToSeriesCount: Number(props.addedToSeriesCount ?? 0),
        initiallyAgreeing: Boolean(props.initiallyAgreeing),
        initiallyDisagreeing: Boolean(props.initiallyDisagreeing),
        initiallyAddedToAnySeries: Boolean(props.initiallyAddedToAnySeries),
        initiallySavedToBookmarks: Boolean(props.initiallySavedToBookmarks),
        initiallyFollowingAuthor: Boolean(props.initiallyFollowingAuthor),
        authorFollowableByPrompt: Boolean(props.authorFollowableByPrompt),
        viewerLastVisitedAt: trimToUndefined(props.viewerLastVisitedAt),
        contentHtml: props.contentHtml ?? "",
        contentLanguageTag: trimToUndefined(props.contentLanguageTag),
        seriesReadingContext: normalizeSeriesReadingContext((props as DiscussionPageProps).seriesReadingContext),
        isQuestion: Boolean(props.isQuestion),
        mindChanged: Boolean(props.mindChanged),
        questionState: parseQuestionState(props.questionState),
        askToAnswerAvailable: props.askToAnswerAvailable === true,
        reviewSubjectName: trimToUndefined(props.reviewSubjectName),
        reviewSubjectSlug: trimToUndefined(props.reviewSubjectSlug),
        reviewRatingHalfStars: numberOrUndefined(props.reviewRatingHalfStars),
        reviewSubjectPublicId: trimToUndefined(props.reviewSubjectPublicId),
        acceptedThoughtPublicId: trimToUndefined(props.acceptedThoughtPublicId),
        // Carried as is: an array means "the server answered" (empty = nothing to anchor), undefined means
        // "not carried", and only the second makes usePassageBlockIds fetch (BL-160).
        passageBlockIds: Array.isArray(props.passageBlockIds) ? props.passageBlockIds : undefined,
    };
}

function mapDiscussionPageDataResponse(data: any): DiscussionPageData | null {
    if (!data) {
        return null;
    }

    const discussionPublicId = trimToUndefined(data.publicId);
    const discussionSlug = trimToUndefined(data.slug);
    const deletedAt = trimToUndefined(data.deletedAt);

    if (!discussionPublicId && !discussionSlug) {
        return null;
    }

    return {
        discussionPublicId,
        discussionSlug,
        authorUsername: normalizeUsernameToUndefined(data.authorUsername),
        authorProfileImageUrl: trimToUndefined(data.authorProfileImageUrl),
        createdAt: trimToUndefined(data.createdAt),
        updatedAt: trimToUndefined(data.updatedAt),
        editedAfterReplyAt: trimToUndefined(data.editedAfterReplyAt),
        authorFeaturedDistinction: trimToUndefined(data.authorFeaturedDistinction),
        authorVoiceOfMonthGroupName: trimToUndefined(data.authorVoiceOfMonthGroupName),
        bodyTextLength: numberOrUndefined(data.bodyTextLength),
        title: data.title?.trim() ?? "",
        pageName: trimToUndefined(data.pageName),
        privateAccess: Boolean(data.privateAccess),
        sensitive: Boolean(data.sensitive),
        spoiler: Boolean(data.spoiler),
        isArchived: Boolean(data.archived),
        isDeleted: Boolean(data.deleted ?? deletedAt),
        archivedAt: trimToUndefined(data.archivedAt),
        deletedAt,
        deletedByModerator: Boolean(data.deletedByModerator),
        deletedReasonType: trimToUndefined(data.deletedReasonType),
        deletedMessage: trimToUndefined(data.deletedMessage),
        agreementsCount: Number(data.agreementsCount ?? 0),
        disagreementsCount: Number(data.disagreementsCount ?? 0),
        argumentsCount: Number(data.argumentsCount ?? 0),
        conversationsCount: numberOrUndefined(data.conversationsCount),
        participantsCount: numberOrUndefined(data.participantsCount),
        quotedByCount: numberOrUndefined(data.quotedByCount),
        relatedCount: numberOrUndefined(data.relatedCount),
        requestsCount: numberOrUndefined(data.requestsCount),
        addedToSeriesCount: Number(data.addedToSeriesCount ?? 0),
        initiallyAgreeing: Boolean(data.initiallyAgreeing),
        initiallyDisagreeing: Boolean(data.initiallyDisagreeing),
        initiallyAddedToAnySeries: Boolean(data.initiallyAddedToAnySeries),
        initiallySavedToBookmarks: Boolean(data.initiallySavedToBookmarks),
        initiallyFollowingAuthor: Boolean(data.initiallyFollowingAuthor),
        authorFollowableByPrompt: Boolean(data.authorFollowableByPrompt),
        viewerLastVisitedAt: trimToUndefined(data.viewerLastVisitedAt),
        contentHtml: data.contentHtml ?? "",
        contentLanguageTag: trimToUndefined(data.languageTag),
        seriesReadingContext: normalizeSeriesReadingContext(data.seriesReadingContext),
        isQuestion: Boolean(data.isQuestion),
        mindChanged: Boolean(data.mindChanged),
        questionState: parseQuestionState(data.questionState),
        askToAnswerAvailable: data.askToAnswerAvailable === true,
        acceptedThoughtPublicId: trimToUndefined(data.acceptedThoughtPublicId),
        reviewSubjectName: trimToUndefined(data.reviewSubjectName),
        reviewSubjectSlug: trimToUndefined(data.reviewSubjectSlug),
        reviewRatingHalfStars: numberOrUndefined(data.reviewRatingHalfStars),
        reviewSubjectPublicId: trimToUndefined(data.reviewSubjectPublicId),
        // BOARD. The soft-navigation path's copy of what `parseInlinedBoardView` reads on a direct
        // load — without it a board renders on reload and VANISHES when you navigate to the same post
        // from inside the app, which is the path the author lands on immediately after publishing.
        // Shape-checked rather than trusted: anything that is not a board with slots becomes undefined,
        // which means "render what the server sent", never "this post has no board".
        boardView: data.board && Array.isArray(data.board.slots) ? (data.board as BoardView) : undefined,
        // POLL. The same soft-navigation copy, read structurally: `DiscussionPageDataResponse` did NOT
        // carry `poll` when this landed (2026-09-14; owed to the backend, poll design §11.3.3), so
        // today this resolves to undefined and the block renders only on a hard load. When the field
        // ships, nothing here changes. Undefined means "render what the server sent", never "no poll".
        pollView: normalizePollView((data as { poll?: unknown }).poll),
        passageBlockIds: Array.isArray(data.passageBlockIds)
            ? (data.passageBlockIds as unknown[]).map((id) => (typeof id === "string" ? id : null))
            : [],
    };
}

export function DiscussionPage(props: DiscussionPageProps) {
    const {
        localizer,
        conversationPublicId: initialConversationPublicId,
        openingUserPresent,
        reactingUserPresent,
        openingUsername,
        reactingUsername,
        openingArgumentPublicId,
        highlightedArgumentPublicId,
        deferInitialLoad = false,
    } = props;

    const initialDiscussionData = React.useMemo(
        () => deferInitialLoad ? null : createDiscussionPageDataFromProps(props),
        [
            deferInitialLoad,
            props.addedToSeriesCount,
            props.agreementsCount,
            props.archivedAt,
            (props as DiscussionPageProps & { authorProfileImageUrl?: string }).authorProfileImageUrl,
            props.authorUsername,
            props.contentHtml,
            props.contentLanguageTag,
            props.createdAt,
            props.deletedAt,
            props.disagreementAgreementTitle,
            props.discussionPublicId,
            props.discussionSlug,
            props.disagreementsCount,
            (props as DiscussionPageProps & { argumentsCount?: number }).argumentsCount,
            props.initiallyAddedToAnySeries,
            props.initiallyAgreeing,
            props.initiallyDisagreeing,
            props.initiallySavedToBookmarks,
            props.initiallyFollowingAuthor,
            props.authorFollowableByPrompt,
            props.viewerLastVisitedAt,
            props.isArchived,
            props.isDeleted,
            props.privateAccess,
            props.sensitive,
            props.spoiler,
            props.title,
            props.pageName,
            props.updatedAt,
            props.editedAfterReplyAt,
            props.authorFeaturedDistinction,
            props.authorVoiceOfMonthGroupName,
            props.bodyTextLength,
            props.isQuestion,
            props.mindChanged,
            props.questionState,
            props.acceptedThoughtPublicId,
            props.askToAnswerAvailable,
            props.passageBlockIds,
        ],
    );
    const [discussionData, setDiscussionData] = React.useState<DiscussionPageData | null>(initialDiscussionData);
    const [isDiscussionLoading, setIsDiscussionLoading] = React.useState<boolean>(deferInitialLoad);
    const [didDiscussionLoadFail, setDidDiscussionLoadFail] = React.useState<boolean>(false);
    const [discussionLoadAttempt, setDiscussionLoadAttempt] = React.useState<number>(0);

    React.useEffect(() => {
        if (deferInitialLoad) {
            return;
        }

        setDiscussionData(initialDiscussionData);
        setIsDiscussionLoading(false);
        setDidDiscussionLoadFail(false);
    }, [deferInitialLoad, initialDiscussionData]);

    const discussionPublicId = discussionData?.discussionPublicId;
    const discussionSlug = discussionData?.discussionSlug ?? trimToUndefined(props.discussionSlug);
    const authorUsername = discussionData?.authorUsername;
    // Round 42 A5: only a LOADED page can say its author is gone; no data yet is not a deleted author.
    const isAuthorDeleted = Boolean(discussionData) && !authorUsername;
    const createdAt = discussionData?.createdAt;
    const updatedAt = discussionData?.updatedAt;
    const editedAfterReplyAt = discussionData?.editedAfterReplyAt;
    const bodyTextLength = discussionData?.bodyTextLength;
    const condensed = props.condensed;
    const title = discussionData?.title ?? "";
    const isArchived = discussionData?.isArchived ?? false;
    // Falls back to the SERVER-RENDERED deletedAt, not just to props.isDeleted.
    //
    // `discussionData` is undefined on the first render, and the island's own fetches fire from that
    // render — so a fallback that ignores deletedAt reports "not deleted" for exactly the window in
    // which the requests are issued. That is not hypothetical: the deleted-rail spec below went red on
    // it. The server always emits data-deleted-at on the tombstone, and toDiscussionPageData derives
    // its own isDeleted the same way (`Boolean(props.isDeleted ?? deletedAt)`), so this keeps the
    // pre-resolution and post-resolution answers identical instead of letting them disagree for a tick.
    const isDeleted = discussionData?.isDeleted
        ?? Boolean(props.isDeleted ?? trimToUndefined((props as DiscussionPageProps).deletedAt));
    const archivedAt = discussionData?.archivedAt;
    const deletedAt = discussionData?.deletedAt;
    const deletedByModerator = discussionData?.deletedByModerator ?? Boolean((props as DiscussionPageProps).deletedByModerator);
    const deletedReasonType = discussionData?.deletedReasonType ?? trimToUndefined((props as DiscussionPageProps).deletedReasonType);
    const deletedMessage = discussionData?.deletedMessage ?? trimToUndefined((props as DiscussionPageProps).deletedMessage);
    const agreementsCount = discussionData?.agreementsCount ?? 0;
    const disagreementsCount = discussionData?.disagreementsCount ?? 0;
    const argumentsCount = discussionData?.argumentsCount ?? 0;
    const sectionCounts = React.useMemo(() => ({
        conversations: discussionData?.conversationsCount,
        participants: discussionData?.participantsCount,
        related: discussionData?.relatedCount,
        requests: discussionData?.requestsCount,
        // Quote post. Unlike `related` this is a REAL number on both delivery paths, so the badge is
        // bound rather than omitted -- and it is also what hides the section entirely at zero, which is
        // the whole reason the count is computed server-side instead of after the first fetch.
        "quoted-by": discussionData?.quotedByCount,
    }), [
        discussionData?.conversationsCount,
        discussionData?.participantsCount,
        discussionData?.relatedCount,
        discussionData?.requestsCount,
        discussionData?.quotedByCount,
    ]);
    const addedToSeriesCount = discussionData?.addedToSeriesCount ?? 0;
    const initiallyAgreeing = discussionData?.initiallyAgreeing ?? false;
    const initiallyDisagreeing = discussionData?.initiallyDisagreeing ?? false;
    const initiallyAddedToAnySeries = discussionData?.initiallyAddedToAnySeries ?? false;
    const initiallySavedToBookmarks = discussionData?.initiallySavedToBookmarks ?? false;
    const initiallyFollowingAuthor = discussionData?.initiallyFollowingAuthor ?? false;
    // Round 37 `2a` (FI-583): latched per post on first sight. The server value is the visit BEFORE this one, but a
    // page-data refetch after this visit's view beacon would return this visit and pull the divider out from under
    // the reader; the latch keeps the marker where it first appeared until the reader moves to another post.
    const [lastVisitLatch, setLastVisitLatch] = React.useState<{ postKey?: string; value?: string }>({});
    const lastVisitPostKey = discussionData?.discussionPublicId ?? discussionData?.discussionSlug;
    if (discussionData && lastVisitPostKey && lastVisitLatch.postKey !== lastVisitPostKey) {
        setLastVisitLatch({ postKey: lastVisitPostKey, value: discussionData.viewerLastVisitedAt });
    }
    const latchedViewerLastVisitedAt = lastVisitLatch.postKey === lastVisitPostKey ? lastVisitLatch.value : undefined;
    // Fail closed: no payload at all means no prompt, never an ungated one.
    const authorFollowableByPrompt = discussionData?.authorFollowableByPrompt ?? false;
    const [followPromptEngagementTriggered, setFollowPromptEngagementTriggered] = React.useState(false);
    const contentHtml = discussionData?.contentHtml ?? "";
    // Share payload only (short-posts design D5, H0-17): a title-less (short) post is named by its own
    // plain text, as the card does. Feeds the share moments and the thoughts' BL-083 host title; the
    // heading keeps `title`. The share chokepoint bounds the length.
    const shareTitle = React.useMemo(() => shareTitleFromHtml(title, contentHtml), [title, contentHtml]);
    // Soft navigation wins over the direct-load payload, matching every other facet field here: after
    // an in-app navigation `props` still describes the page the island mounted on, not the one it is
    // showing now.
    const boardView = discussionData?.boardView ?? props.boardView;
    const pollView = discussionData?.pollView ?? props.pollView;
    // FS-7 / FI-319. `isBoard` / `isPoll` are the bits that disambiguate `boardView === undefined`:
    // "not a board post" and "a board post whose inline payload never arrived" are the same value
    // without them. They were computed at mount, logged, passed down — and then read by nothing, so
    // the degraded load rendered as an ordinary post with the server's grid DELETED by `createRoot()`.
    //
    // Both conditions below are load-bearing:
    //   - `deferInitialLoad` is the shell-request path, where the payload is absent by construction
    //     rather than by failure (the mount-time WARN is suppressed there for the same reason);
    //   - `discussionData === initialDiscussionData` means nothing has replaced the payload the
    //     island mounted with. `props.isBoard` describes the post the island mounted on, so after an
    //     in-app navigation it is stale, and using it would mark every post reached from a board post.
    //     Reference-equality, not a null check: on the deferred path both are `null` and equal, which
    //     is exactly why `deferInitialLoad` cannot be dropped from this predicate.
    const facetFlagsDescribeThisPost = !deferInitialLoad && discussionData === initialDiscussionData;
    const boardPayloadMissing = facetFlagsDescribeThisPost && Boolean(props.isBoard) && !boardView;
    const pollPayloadMissing = facetFlagsDescribeThisPost && Boolean(props.isPoll) && !pollView;
    const contentLanguageTag = discussionData?.contentLanguageTag;
    const seriesReadingContext = discussionData?.seriesReadingContext;
    const privateAccess = discussionData?.privateAccess ?? false;
    const isSensitive = discussionData?.sensitive ?? Boolean(props.sensitive);
    const isSpoiler = discussionData?.spoiler ?? Boolean((props as DiscussionPageProps & { spoiler?: boolean }).spoiler);
    // --- QUESTION facet (designs/01-now/2026-07-29-post-facets-question-facet-design.md §11.1). ---
    // The state changes from inside this page — accept, unaccept, mark answered, reopen — so it is local
    // state rather than a straight read. Two properties keep it honest:
    //
    //  - it is only ever WRITTEN from a server response (`QuestionStateView`), never derived from
    //    `acceptedThoughtPublicId` or from the list's contents. The server resolves `ANSWERED` through the
    //    accepted answer's VISIBILITY, which the client cannot see (Q-D10);
    //  - the seeding effect runs in ONE direction. A fresh payload (soft nav, reload, retry) wins; nothing
    //    writes the local value back into the payload. Two effects syncing a value both ways do not
    //    converge, they swap — that is the React #185 shape rule 31 exists for.
    //
    // `discussionData.acceptedThoughtPublicId` is deliberately carried but NOT read here: the accepted
    // badge comes off each answer's own `accepted` field, and comparing ids to place it would badge an
    // answer that has since been deleted. Do not "simplify" it into that comparison.
    const isQuestion = discussionData?.isQuestion ?? Boolean(props.isQuestion);
    // Live page data first, mount props second - the same order every other field here uses, so an
    // in-app soft navigation and a direct load agree.
    const hasMindChanged = discussionData?.mindChanged ?? Boolean(props.mindChanged);
    const serverQuestionState = discussionData?.questionState;
    const [questionState, setQuestionState] = React.useState<QuestionState | undefined>(serverQuestionState);
    React.useEffect(() => {
        setQuestionState(serverQuestionState);
    }, [serverQuestionState]);
    /**
     * PASSAGE anchors: the summaries the markers on the post body draw from, derived from the tree's
     * loaded list. Keyed by content before it is stored, so a list re-fetch that changed no anchor keeps
     * the same array and re-renders nothing above (rule 31, one layer up from a store).
     */
    const [passageAnchoredThoughts, setPassageAnchoredThoughts] = React.useState<PassageAnchoredThoughtSummary[]>([]);
    const handleThoughtItemsChanged = React.useCallback((items: OpeningThoughtWithGroupMatchesDTO[]) => {
        const next: PassageAnchoredThoughtSummary[] = [];
        for (const item of items) {
            const thought = item?.openingArgument;
            const blockId = thought?.anchorBlockId?.trim();
            const publicId = thought?.publicId?.trim();
            const state = blockId ? parsePassageAnchorState(thought?.anchorState) : null;
            if (blockId && publicId && state) {
                next.push({ publicId, blockId, state, authorUsername: thought?.username ?? undefined });
            }
        }
        setPassageAnchoredThoughts((previous) => {
            const same = previous.length === next.length && previous.every((entry, index) =>
                entry.publicId === next[index].publicId
                && entry.blockId === next[index].blockId
                && entry.state === next[index].state
                && entry.authorUsername === next[index].authorUsername);
            return same ? previous : next;
        });
    }, []);

    const handleQuestionStateChanged = React.useCallback((view: QuestionStateView) => {
        setQuestionState(view.state);
    }, []);

    // --- REVIEW facet (§5.7). ---
    // An override rather than a mirror of the server's copy. There is deliberately no effect syncing
    // this back from `discussionData`: two useEffects writing each other's source do not converge, they
    // swap, and that is the shape that crashed the main header twice (AGENTS.md rule 31). The override
    // is set once by a successful save and cleared only by a real navigation, which remounts anyway.
    const [reviewFacetOverride, setReviewFacetOverride] =
        React.useState<{ subjectName?: string; ratingHalfStars?: number } | null>(null);
    const handleReviewFacetChanged = React.useCallback(
        (next: { subjectName?: string; ratingHalfStars?: number }) => setReviewFacetOverride(next),
        [],
    );
    const agreementAgreementTitle = trimToUndefined(props.agreementAgreementTitle) ?? localizer("discussion.agreement");
    const disagreementAgreementTitle = trimToUndefined(props.disagreementAgreementTitle) ?? localizer("discussion.disagreement");
    const pageWarningCopy = React.useMemo(
        () => getPageContentWarningCopy(localizer, { isSensitive, isSpoiler, kind: "discussion" }),
        [isSensitive, isSpoiler, localizer],
    );

    const normalizedOpeningUsername = normalizeUsernameToUndefined(openingUsername);
    const normalizedReactingUsername = normalizeUsernameToUndefined(reactingUsername);
    const propHighlightedArgumentPublicId = trimToUndefined(highlightedArgumentPublicId);
    const shouldInferConversationParticipantsFromArgumentOrder = Boolean(
        initialConversationPublicId && (
            !openingUserPresent
            || !reactingUserPresent
            || !normalizedOpeningUsername
            || !normalizedReactingUsername
        )
    );

    const loggedInUsername = MetaHandler.getLoggedInUsername();
    const isUserLoggedIn = MetaHandler.isUserLoggedIn();
    /**
     * A short post (round 31 D5: `title IS NULL`). Both delivery paths normalize a null title to `""`
     * (`discussion.jte` emits `data-title=""` for it), so an empty title is the only signal the page has;
     * a legacy `''`-titled long post would read as short too, and prod has none (work order A1).
     *
     * No `isDiscussionLoading` term (FI-547 (1)): the deferred path commits the arrived data while that
     * flag is still true, so gating on it rendered the section controls row on a short post and then
     * removed it. Without page data `discussionPublicId` is undefined (and the skeleton renders), so the
     * load state adds nothing here.
     */
    const isShortPostPage = title === "" && !isDeleted && Boolean(discussionPublicId);
    const initialRouteState = React.useMemo<DiscussionRouteState>(() => ({
        section: props.section,
        conversationPublicId: initialConversationPublicId,
        highlightedArgumentPublicId: propHighlightedArgumentPublicId ?? getHighlightedArgumentPublicIdFromHash(),
        selectedGroupPublicIds: typeof window === "undefined"
            ? []
            : parseSelectedGroupPublicIdsFromSearch(window.location.search),
        originGroupPublicId: typeof window === "undefined"
            ? undefined
            : parseOriginGroupPublicIdFromSearch(window.location.search),
        originSeriesPublicId: typeof window === "undefined"
            ? undefined
            : parseOriginSeriesPublicIdFromSearch(window.location.search),
        openingArgumentOrdering: typeof window === "undefined"
            ? "new"
            : parseOpeningArgumentOrderingFromSearch(window.location.search),
    }), [initialConversationPublicId, propHighlightedArgumentPublicId, props.section]);

    const {state: routeState, setState: setRouteState} = useHistoryBackedState<DiscussionRouteState>({
        initialState: initialRouteState,
        readState: (location, fallback) => readDiscussionRouteState(location, fallback, discussionSlug),
        buildUrl: (state, location) => buildDiscussionPageUrl(state, location, discussionSlug, openingArgumentPublicId),
        areEqual: (left, right) => left.section === right.section
            && left.conversationPublicId === right.conversationPublicId
            && left.highlightedArgumentPublicId === right.highlightedArgumentPublicId
            && areNormalizedStringArraysEqual(left.selectedGroupPublicIds, right.selectedGroupPublicIds)
            && left.originGroupPublicId === right.originGroupPublicId
            && left.originSeriesPublicId === right.originSeriesPublicId
            && normalizeDiscussionOpeningThoughtOrdering(left.openingArgumentOrdering) === normalizeDiscussionOpeningThoughtOrdering(right.openingArgumentOrdering),
    });

    const currentSection = routeState.section;
    /**
     * FI-547 (2), operator decision 2026-09-25 ("ignore the param"): a short post's page has no section
     * nav (SV-141 D1), so a `participants` arrival renders the post's default body (`conversations`)
     * instead of a roster nobody can see the way out of. RENDER-ONLY: the route state and the URL are
     * left exactly as they arrived, so nothing here writes history (rule 33) and a titled post is
     * untouched. Scoped to `participants`; the other destination sections are real notification and
     * share entries and are not coerced. Pinned by `DiscussionPage.shortPost.spec.tsx`.
     */
    const renderedSection = isShortPostPage && currentSection === "participants" ? "conversations" : currentSection;
    // Round 7 slice 2c (D5/A4): one thought `count_view` per focused-anchor load of
    // `/discussion/{slug}/thought/{id}` (the `opening` section with an anchor id), never an in-list impression.
    useFocusedThoughtViewBeacon({ section: currentSection, thoughtPublicId: openingArgumentPublicId, isDeleted });
    const selectedConversationPublicId = routeState.conversationPublicId;
    const highlightedConversationArgumentPublicId = routeState.highlightedArgumentPublicId;

    /**
     * A cold arrival at a section route lands ON the section it asked for (`DISCUSSION-10`).
     *
     * <p>`/related` and its siblings are first-class shareable URLs, but they re-render the whole
     * opening post above their own panel: measured 2026-08-13, the panel sat at y≈4,743 of a 4,957px
     * desktop page, and further down on mobile where the tab strip is not sticky either. A visitor who
     * asked for related posts got a long essay they had already decided about, with nothing saying they
     * were on a different route.
     *
     * <p>Four conditions, and each one exists to stop this firing where it should not:
     * <ul>
     *   <li><b>`props.section`, never `currentSection`</b> — the section the island was MOUNTED with. A
     *       tab click, a soft navigation and a Back all change `currentSection` and must not re-scroll;
     *       the click path is already at the strip (it is `md:sticky`).</li>
     *   <li><b>A destination section only.</b> The reading routes are where the article IS the
     *       destination.</li>
     *   <li><b>Once per page instance</b>, via the ref below.</li>
     *   <li><b>Only from the top of an un-anchored document.</b> A restored scroll position or a
     *       `#thought-…` hash is somebody else's answer to the same question, and overriding it would
     *       be this row's defect in the other direction.</li>
     * </ul>
     *
     * <p>The target is the section control ROW rather than the panel: it is the element that names the
     * active section, it sits immediately above the panel, and it exists before any list has resolved —
     * so the destination does not depend on a fetch landing. Nothing here writes history (rule 33); the
     * effect only moves the viewport.
     */
    const coldArrivalScrollDoneRef = React.useRef(false);
    const shouldScrollToArrivalSection = DEEP_LINKED_DISCUSSION_SECTIONS.has(props.section ?? "");
    /**
     * The live section-controls row, kept so a selection made from the docked bar can be taken TO it
     * (`DISCUSSION-48`). The callback ref above already receives the node; it just never kept it.
     */
    const sectionControlsNodeRef = React.useRef<HTMLDivElement | null>(null);
    const sectionControlsRef = React.useCallback((node: HTMLDivElement | null) => {
        sectionControlsNodeRef.current = node;
        if (!node || !shouldScrollToArrivalSection || coldArrivalScrollDoneRef.current) {
            return;
        }
        if (typeof window !== "undefined" && (window.scrollY > 0 || window.location.hash)) {
            coldArrivalScrollDoneRef.current = true;
            return;
        }

        coldArrivalScrollDoneRef.current = true;
        // Feature-detected rather than assumed: this runs during commit on every discussion page, and a
        // host without it would take the whole island down for a scroll position.
        if (typeof node.scrollIntoView === "function") {
            node.scrollIntoView({ block: "start", behavior: "auto" });
        }
    }, [shouldScrollToArrivalSection]);

    /**
     * `DISCUSSION-48` — a selection made from the DOCKED BAR is an arrival, not a tab click.
     *
     * <p>The cold-arrival scroller above deliberately excludes the click path, and its comment gives the
     * reason: "the click path is already at the strip (it is `md:sticky`)". That is true of the desktop
     * strip and FALSE of the phone's bar, which is `fixed bottom-0` and portaled to the body — its
     * trigger is reachable from any scroll offset in the document, while the panel it switches lives
     * below the entire article. So a reader deep in a 110-thought thread tapped Participants and the
     * viewport did not move; worse, swapping 110 thoughts for a short roster shortens the document, so
     * the browser clamps the offset and the page appears to jump somewhere nobody asked for.
     *
     * <p>The trigger is the BAR'S OWN handler and never `currentSection` changing, which is what keeps
     * the existing no-re-scroll rule intact for Back, soft navigation and the strip's own anchors.
     *
     * <p>Deferred one frame, because the destination is only correct after the swap has laid out: the
     * new panel decides the document height, and therefore whether the row can reach the top of the
     * viewport at all. `scrollIntoView` then clamps for us. `behavior: "auto"`, matching the cold
     * arrival — a scroll animation nobody asked for is its own finding.
     *
     * <p>Nothing here writes history (rule 33): this moves the viewport and nothing else.
     */
    const scrollSectionControlsIntoView = React.useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }
        const run = () => {
            const node = sectionControlsNodeRef.current;
            if (node && typeof node.scrollIntoView === "function") {
                node.scrollIntoView({ block: "start", behavior: "auto" });
            }
        };
        if (typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(run);
            return;
        }
        run();
    }, []);

    /**
     * The real address of each section, for the section row's anchors (`DISCUSSION-14`).
     *
     * Deliberately the SAME builder `useHistoryBackedState` uses above, applied to the same route
     * state with only `section` swapped. That is what makes the address a middle-click opens
     * identical, by construction, to the address a plain click produces — a second, hand-written
     * path here would be a second thing to keep in step.
     *
     * Nothing here writes history: the anchor's default is prevented on a plain click and the change
     * goes through `setRouteState` exactly as before (rule 33).
     */
    const buildSectionHref = React.useCallback((section: string) => {
        if (typeof window === "undefined" || !discussionSlug) {
            return undefined;
        }
        return buildDiscussionPageUrl(
            { ...routeState, section },
            window.location,
            discussionSlug,
            openingArgumentPublicId,
        );
    }, [discussionSlug, openingArgumentPublicId, routeState]);

    // A selected conversation always opens as a single full view in the main column (the same
    // presentation on every viewport), with its own Back button to return to the opening arguments.
    // The right discovery rail stays as-is alongside it, exactly like every other discussion section.

    // Track the open conversation so closing it (Back) can restore focus to the View control
    // that opened it (a11y), and a helper that re-focuses that control by its conversation id.
    const openConversationIdRef = React.useRef<string | undefined>(undefined);
    React.useEffect(() => {
        openConversationIdRef.current = currentSection === "conversation" ? selectedConversationPublicId : undefined;
    }, [currentSection, selectedConversationPublicId]);

    const focusConversationTrigger = React.useCallback((conversationId?: string) => {
        if (!conversationId || typeof document === "undefined") {
            return;
        }
        const escaped = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(conversationId) : conversationId;
        window.requestAnimationFrame(() => {
            const trigger = document.querySelector(`[data-playwright-id="thought-view-conversation-button"][data-conversation-public-id="${escaped}"]`);
            if (trigger instanceof HTMLElement) {
                trigger.focus();
            }
        });
    }, []);
    const selectedGroupPublicIds = React.useMemo(
        () => normalizeDiscussionGroupPublicIds(routeState.selectedGroupPublicIds),
        [routeState.selectedGroupPublicIds],
    );
    // `originGroupPublicId` (URL: `fromGroupPublicId`) is PROVENANCE ONLY — it records that this visit
    // came from a group and is deliberately not read here. It used to seed the thought group filter;
    // that pre-filter was removed 2026-07-30 because arriving from a group silently hid every reply
    // from outside it. The field stays in route state so `buildDiscussionPageUrl` round-trips it
    // across in-page navigation. See the 2026-07-30 amendment in
    // designs/04-live/2026-05-02-discussion-group-context-navigation-and-auto-filter-design.md.
    const originSeriesPublicId = React.useMemo(
        () => trimToUndefined(routeState.originSeriesPublicId),
        [routeState.originSeriesPublicId],
    );
    const openingArgumentOrdering = React.useMemo(
        () => normalizeDiscussionOpeningThoughtOrdering(routeState.openingArgumentOrdering),
        [routeState.openingArgumentOrdering],
    );

    React.useEffect(() => {
        if (!deferInitialLoad) {
            return;
        }

        const discussionRouteKey = trimToUndefined(props.discussionSlug) ?? trimToUndefined(props.discussionPublicId);
        if (!discussionRouteKey) {
            setDiscussionData(null);
            setIsDiscussionLoading(false);
            setDidDiscussionLoadFail(true);
            return;
        }

        let cancelled = false;
        setIsDiscussionLoading(true);
        setDidDiscussionLoadFail(false);

        void ClientFactory.getDiscussionPageData<any>(
            discussionRouteKey,
            originSeriesPublicId
                ? { params: { fromSeriesPublicId: originSeriesPublicId } }
                : undefined,
        )
            .then((response) => {
                if (cancelled) {
                    return;
                }

                const nextDiscussionData = mapDiscussionPageDataResponse(response.data);
                if (!nextDiscussionData) {
                    setDiscussionData(null);
                    setDidDiscussionLoadFail(true);
                    return;
                }

                setDiscussionData(nextDiscussionData);
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                logger.error("[DiscussionPage] failed to load discussion page data", error);
                setDiscussionData(null);
                setDidDiscussionLoadFail(true);
            })
            .finally(() => {
                if (!cancelled) {
                    setIsDiscussionLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [deferInitialLoad, discussionLoadAttempt, originSeriesPublicId, props.discussionPublicId, props.discussionSlug]);

    const [currentAddedToSeriesCount, setCurrentAddedToSeriesCount] = React.useState<number>(
        getVisibleDiscussionSeriesCount(addedToSeriesCount, initiallySavedToBookmarks),
    );
    const [isAddedToAnySeries, setIsAddedToAnySeries] = React.useState<boolean>(initiallyAddedToAnySeries ?? false);

    const [prependSeries, setPrependSeries] = React.useState<SeriesDTO[]>([]);
    const [removedSeriesIds, setRemovedSeriesIds] = React.useState<Set<string>>(new Set());

    const [prependGroups, setPrependGroups] = React.useState<GroupDTO[]>([]);
    const [removedGroupIds, setRemovedGroupIds] = React.useState<Set<string>>(new Set());
    const [removedDiscussionRequestIds, setRemovedDiscussionRequestIds] = React.useState<Set<string>>(new Set());
    const [fetchedDiscussionGroups, setFetchedDiscussionGroups] = React.useState<GroupDTO[]>([]);
    const [discussionGroupsSourcePublicId, setDiscussionGroupsSourcePublicId] = React.useState<string | undefined>(undefined);
    const [hasLoadedDiscussionGroups, setHasLoadedDiscussionGroups] = React.useState<boolean>(false);
    const [hasLoadedAllDiscussionGroups, setHasLoadedAllDiscussionGroups] = React.useState<boolean>(false);
    const [isDiscussionGroupsLoading, setIsDiscussionGroupsLoading] = React.useState<boolean>(false);
    // participants refresh key: bumping this forces ParticipantsListDisplayer to remount & refetch
    const [participantsRefreshKey, setParticipantsRefreshKey] = React.useState<number>(0);

    // Only an ACTIVE filter needs the discussion's full group list up front (to sanitize the selection
    // against it). A group-originated visit no longer does: the origin param neither filters nor gets
    // validated, so it must not cost a paginated sweep of every attached group on arrival. The list
    // still loads on demand when the filter control is opened.
    const shouldLoadAllDiscussionGroupsInitially = selectedGroupPublicIds.length > 0;

    const appendDistinctDiscussionGroups = React.useCallback((existingGroups: GroupDTO[], nextGroups: GroupDTO[]) => {
        const grouped = new Map<string, GroupDTO>();
        [...existingGroups, ...nextGroups].forEach((group) => {
            const publicId = group.publicId?.trim();
            if (!publicId || grouped.has(publicId)) {
                return;
            }
            grouped.set(publicId, group);
        });
        return Array.from(grouped.values());
    }, []);

    const loadDiscussionGroupsPage = React.useCallback(async (cutoffAddedAt?: string) => {
        // A deleted discussion's sections are suppressed server-side (discussion.jte gates the
        // groups/series/tags blocks on !isDeleted) and every one of these endpoints answers 400
        // "Deleted posts are no longer available for this action". Firing them buys nothing but
        // origin load and FRONTEND error noise. See the D3 section of
        // designs/01-now/2026-08-01-draft-body-integrity-tombstone-islands-and-schedule-validation-design.md
        if (!discussionPublicId || isDeleted) {
            return [] as GroupDTO[];
        }

        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        const resp = await api.groupsForDiscussion(discussionPublicId, cutoffAddedAt ?? undefined);
        return Array.isArray(resp.data) ? resp.data : [];
    }, [discussionPublicId, isDeleted]);

    const ensureAllDiscussionGroupsLoaded = React.useCallback(async () => {
        if (!discussionPublicId || hasLoadedAllDiscussionGroups || isDiscussionGroupsLoading) {
            return;
        }

        setIsDiscussionGroupsLoading(true);
        try {
            let collectedGroups = [...fetchedDiscussionGroups];
            let paginationParameter = fetchedDiscussionGroups[fetchedDiscussionGroups.length - 1]?.paginationParameter ?? undefined;
            const seenPaginationParameters = new Set<string>();

            if (!hasLoadedDiscussionGroups) {
                const firstPage = await loadDiscussionGroupsPage();
                collectedGroups = appendDistinctDiscussionGroups([], firstPage);
                setFetchedDiscussionGroups(collectedGroups);
                setHasLoadedDiscussionGroups(true);
                paginationParameter = firstPage[firstPage.length - 1]?.paginationParameter ?? undefined;
                if (!firstPage.length || !paginationParameter) {
                    setHasLoadedAllDiscussionGroups(true);
                    return;
                }
            }

            while (paginationParameter && !seenPaginationParameters.has(paginationParameter)) {
                seenPaginationParameters.add(paginationParameter);
                const page = await loadDiscussionGroupsPage(paginationParameter);
                if (!page.length) {
                    break;
                }

                collectedGroups = appendDistinctDiscussionGroups(collectedGroups, page);
                paginationParameter = page[page.length - 1]?.paginationParameter ?? undefined;
                if (!paginationParameter || seenPaginationParameters.has(paginationParameter)) {
                    break;
                }
            }

            setFetchedDiscussionGroups(collectedGroups);
            setHasLoadedDiscussionGroups(true);
            setHasLoadedAllDiscussionGroups(true);
        } catch (e) {
            logger.error("[DiscussionPage] failed to fetch all discussion groups", e);
            if (!hasLoadedDiscussionGroups) {
                setFetchedDiscussionGroups([]);
                setHasLoadedDiscussionGroups(true);
            }
        } finally {
            setIsDiscussionGroupsLoading(false);
        }
    }, [appendDistinctDiscussionGroups, discussionPublicId, fetchedDiscussionGroups, hasLoadedAllDiscussionGroups, hasLoadedDiscussionGroups, isDiscussionGroupsLoading, loadDiscussionGroupsPage]);

    React.useEffect(() => {
        if (!discussionPublicId) return;

        const unsubSeriesAdd = onSeriesAddedToDiscussion((payload: any) => {
            try {
                if (!payload) return;
                if (payload.discussionPublicId !== discussionPublicId) return;
                const incomingSeries: SeriesDTO | undefined = payload.series;
                if (!incomingSeries?.publicId) return;
                if (isBookmarksSeries(incomingSeries)) return;

                setPrependSeries(prev => {
                    if (prev.some(p => p.publicId === incomingSeries.publicId)) return prev;
                    return [incomingSeries, ...prev];
                });
                setCurrentAddedToSeriesCount(prev => prev + 1);
                setIsAddedToAnySeries(true);

                setRemovedSeriesIds(prev => {
                    if (!prev.has(incomingSeries.publicId!)) return prev;
                    const n = new Set(prev);
                    n.delete(incomingSeries.publicId!);
                    return n;
                });
            } catch (e) {
                logger.error("[DiscussionPage] onSeriesAddedToDiscussion handler error", e);
            }
        });

        const unsubSeriesRemove = onSeriesRemovedFromDiscussion((payload: any) => {
            try {
                if (!payload) return;
                if (payload.discussionPublicId !== discussionPublicId) return;
                const incomingSeries: SeriesDTO | undefined = payload.series;
                if (!incomingSeries?.publicId) return;
                if (isBookmarksSeries(incomingSeries)) return;

                setPrependSeries(prev => prev.filter(p => p.publicId !== incomingSeries.publicId));
                setCurrentAddedToSeriesCount(prev => Math.max(0, prev - 1));
                setRemovedSeriesIds(prev => {
                    if (prev.has(incomingSeries.publicId!)) return prev;
                    const n = new Set(prev);
                    n.add(incomingSeries.publicId!);
                    return n;
                });
            } catch (e) {
                logger.error("[DiscussionPage] onSeriesRemovedFromDiscussion handler error", e);
            }
        });

        return () => {
            unsubSeriesAdd();
            unsubSeriesRemove();
        };
    }, [discussionPublicId]);

    React.useEffect(() => {
        if (!discussionPublicId) {
            setDiscussionGroupsSourcePublicId(undefined);
            setFetchedDiscussionGroups([]);
            setHasLoadedDiscussionGroups(false);
            setHasLoadedAllDiscussionGroups(false);
            return;
        }

        let cancelled = false;

        const loadDiscussionGroupsPreview = async () => {
            setIsDiscussionGroupsLoading(true);
            try {
                const firstPage = await loadDiscussionGroupsPage();
                if (cancelled) {
                    return;
                }

                setFetchedDiscussionGroups(firstPage);
                setHasLoadedDiscussionGroups(true);

                const nextPaginationParameter = firstPage[firstPage.length - 1]?.paginationParameter ?? undefined;
                if (!firstPage.length || !nextPaginationParameter) {
                    setHasLoadedAllDiscussionGroups(true);
                    return;
                }

                if (shouldLoadAllDiscussionGroupsInitially) {
                    let collectedGroups = [...firstPage];
                    let paginationParameter: string | undefined = nextPaginationParameter;
                    const seenPaginationParameters = new Set<string>();

                    while (paginationParameter && !seenPaginationParameters.has(paginationParameter)) {
                        seenPaginationParameters.add(paginationParameter);
                        const page = await loadDiscussionGroupsPage(paginationParameter);
                        if (cancelled || !page.length) {
                            break;
                        }

                        collectedGroups = appendDistinctDiscussionGroups(collectedGroups, page);
                        paginationParameter = page[page.length - 1]?.paginationParameter ?? undefined;
                        if (!paginationParameter || seenPaginationParameters.has(paginationParameter)) {
                            break;
                        }
                    }

                    if (!cancelled) {
                        setFetchedDiscussionGroups(collectedGroups);
                        setHasLoadedAllDiscussionGroups(true);
                    }
                }
            } catch (e) {
                // Groups preview is a non-critical enrichment. Only genuine server faults (5xx) are
                // actionable; expected failures for anonymous/email-link/throttled clients are noise.
                const status = (e as { response?: { status?: number }; status?: number })?.response?.status
                    ?? (e as { status?: number })?.status;
                const isServerFault = status != null && status >= 500;
                const logFn = isServerFault ? logger.error : logger.debug;
                logFn("[DiscussionPage] failed to fetch discussion groups preview", e);
                if (!cancelled) {
                    setFetchedDiscussionGroups([]);
                    setHasLoadedDiscussionGroups(true);
                }
            } finally {
                if (!cancelled) {
                    setIsDiscussionGroupsLoading(false);
                }
            }
        };

        setDiscussionGroupsSourcePublicId(discussionPublicId);
        setFetchedDiscussionGroups([]);
        setHasLoadedDiscussionGroups(false);
        setHasLoadedAllDiscussionGroups(false);
        void loadDiscussionGroupsPreview();

        return () => {
            cancelled = true;
        };
    }, [appendDistinctDiscussionGroups, discussionPublicId, loadDiscussionGroupsPage, shouldLoadAllDiscussionGroupsInitially]);

    const orderedDiscussionGroups = React.useMemo(
        () => mergeDiscussionGroups(fetchedDiscussionGroups, prependGroups, removedGroupIds),
        [fetchedDiscussionGroups, prependGroups, removedGroupIds],
    );
    const currentDiscussionGroups = React.useMemo(
        () => discussionGroupsSourcePublicId === discussionPublicId ? orderedDiscussionGroups : [],
        [discussionGroupsSourcePublicId, discussionPublicId, orderedDiscussionGroups],
    );
    const discussionGroupsSummaryProps = React.useMemo(() => ({
        groups: currentDiscussionGroups,
        localizer,
    }), [currentDiscussionGroups, localizer]);
    const discussionGroupsByPublicId = React.useMemo<Record<string, GroupDTO>>(() => {
        return currentDiscussionGroups.reduce<Record<string, GroupDTO>>((accumulator, group) => {
            const publicId = group.publicId?.trim();
            if (!publicId) {
                return accumulator;
            }
            accumulator[publicId] = group;
            return accumulator;
        }, {});
    }, [currentDiscussionGroups]);

    /**
     * The series summary shares the group summary's slot (`M-2`, discoverability phase 1). One
     * block above the post title, not a second competing position, and NOT rendered at all when the
     * post is in no series — so with the feature unused this memo's output, and the page's request
     * log, are byte-identical to what they were before it existed.
     *
     * `addedToSeriesCount` is the page's own live count, already maintained here for the section
     * selector label, so the gate costs no extra read.
     */
    const discussionSeriesSummary = React.useMemo(() => {
        if (Math.max(0, currentAddedToSeriesCount) <= 0) {
            return null;
        }
        return (
            <DiscussionSeriesSummary
                discussionPublicId={discussionPublicId}
                seriesCount={currentAddedToSeriesCount}
                isDeleted={isDeleted}
                localizer={localizer}
            />
        );
    }, [currentAddedToSeriesCount, discussionPublicId, isDeleted, localizer]);

    const discussionContextSummary = React.useMemo(() => {
        if (discussionGroupsSourcePublicId !== discussionPublicId || !hasLoadedDiscussionGroups) {
            // A fragment, so the no-series case emits exactly the skeleton it always did.
            return (
                <>
                    <DiscussionGroupSummarySkeleton showPrivateAccessBadge={privateAccess} />
                    {discussionSeriesSummary}
                </>
            );
        }

        if (!currentDiscussionGroups.length) {
            return discussionSeriesSummary;
        }

        if (!discussionSeriesSummary) {
            return <DiscussionGroupSummary {...discussionGroupsSummaryProps} showPrivateAccessBadge={privateAccess} />;
        }

        // U39 (`DISCUSSION-96`, `-123`): both kinds of place → ONE line naming both, with the detail in its
        // popover, instead of two near-identical avatar rows above the title.
        return (
            <DiscussionProvenanceSummary
                groups={currentDiscussionGroups}
                seriesCount={Math.max(0, currentAddedToSeriesCount)}
                discussionPublicId={discussionPublicId}
                showPrivateAccessBadge={privateAccess}
                localizer={localizer}
            />
        );
    }, [currentAddedToSeriesCount, currentDiscussionGroups, discussionGroupsSourcePublicId, discussionPublicId, discussionGroupsSummaryProps, discussionSeriesSummary, hasLoadedDiscussionGroups, localizer, privateAccess]);

    const discussionSeriesCount = React.useMemo(
        () => Math.max(0, currentAddedToSeriesCount),
        [currentAddedToSeriesCount],
    );

    React.useEffect(() => {
        if (!discussionPublicId) return;

        const unsubGroupAdd = onGroupAddedToDiscussion((payload: any) => {
            try {
                if (!payload) return;
                if (payload.discussionPublicId !== discussionPublicId) return;
                const incomingGroup: GroupDTO | undefined = payload.group;
                if (!incomingGroup?.publicId) return;

                setPrependGroups(prev => {
                    if (prev.some(p => p.publicId === incomingGroup.publicId)) return prev;
                    return [incomingGroup, ...prev];
                });

                setRemovedGroupIds(prev => {
                    if (!prev.has(incomingGroup.publicId!)) return prev;
                    const n = new Set(prev);
                    n.delete(incomingGroup.publicId!);
                    return n;
                });
            } catch (e) {
                logger.error("[DiscussionPage] onGroupAddedToDiscussion handler error", e);
            }
        });

        const unsubGroupRemove = onGroupRemovedFromDiscussion((payload: any) => {
            try {
                if (!payload) return;
                if (payload.discussionPublicId !== discussionPublicId) return;
                const incomingGroup: GroupDTO | undefined = payload.group;
                if (!incomingGroup?.publicId) return;

                setPrependGroups(prev => prev.filter(p => p.publicId !== incomingGroup.publicId));
                setRemovedGroupIds(prev => {
                    if (prev.has(incomingGroup.publicId!)) return prev;
                    const n = new Set(prev);
                    n.add(incomingGroup.publicId!);
                    return n;
                });
            } catch (e) {
                logger.error("[DiscussionPage] onGroupRemovedFromDiscussion handler error", e);
            }
        });

        return () => {
            unsubGroupAdd();
            unsubGroupRemove();
        };
    }, [discussionPublicId]);

    React.useEffect(() => {
        if (!discussionPublicId) return;

        return onDiscussionAddToGroupRequestResolved((payload: any) => {
            try {
                if (!payload || payload.discussionPublicId !== discussionPublicId) return;
                const ids = Array.isArray(payload.requestPublicIds) ? payload.requestPublicIds.filter(Boolean) : [];
                if (!ids.length) return;
                setRemovedDiscussionRequestIds(prev => {
                    const next = new Set(prev);
                    ids.forEach((id: string) => next.add(id));
                    return next;
                });
            } catch (e) {
                logger.error("[DiscussionPage] onDiscussionAddToGroupRequestResolved handler error", e);
            }
        });
    }, [discussionPublicId]);

    // subscribe to viewer agreement changes to refresh participants list when visible
    React.useEffect(() => {
        if (!discussionPublicId) return;
        return onDiscussionViewerAgreementChanged((payload: any) => {
            try {
                if (!payload) return;
                if (payload.discussionPublicId !== discussionPublicId) return;
                // T1 of the follow-author prompt: taking a stance on someone's post is demonstrated
                // affinity, and it is the cheapest possible trigger because the event already exists
                // and already fires here. Only a stance being SET counts — clearing one is not
                // affinity. Latched on: the prompt owns its own dismissal from here.
                // designs/01-now/2026-09-06-follow-author-prompt-design.md §3
                if (payload.agreement === "AGREE" || payload.agreement === "DISAGREE") {
                    setFollowPromptEngagementTriggered(true);
                }
                // only refresh when participants view is showing
                if (currentSection === "participants") {
                    setParticipantsRefreshKey(k => k + 1);
                }
            } catch (e) {
                logger.error("[DiscussionPage] onDiscussionViewerAgreementChanged handler error", e);
            }
        });
    }, [discussionPublicId, currentSection]);

    // Sanitize the ACTIVE filter against the discussion's real groups (a shared or stale URL can name a
    // group this discussion is not in, which would otherwise filter everything away). The origin param
    // is intentionally not touched here — it renders nothing, so a stale one is inert and not worth the
    // eager group load that validating it would require.
    React.useEffect(() => {
        if (selectedGroupPublicIds.length === 0) {
            return;
        }
        if (!hasLoadedDiscussionGroups || !hasLoadedAllDiscussionGroups) {
            return;
        }

        const validGroupIds = new Set(Object.keys(discussionGroupsByPublicId));
        const sanitizedSelectedGroupPublicIds = selectedGroupPublicIds.filter((groupPublicId) => validGroupIds.has(groupPublicId));

        if (areNormalizedStringArraysEqual(selectedGroupPublicIds, sanitizedSelectedGroupPublicIds)) {
            return;
        }

        setRouteState((previous) => ({
            ...previous,
            selectedGroupPublicIds: sanitizedSelectedGroupPublicIds,
        }), { replace: "canonicalize" });
    }, [discussionGroupsByPublicId, hasLoadedAllDiscussionGroups, hasLoadedDiscussionGroups, selectedGroupPublicIds, setRouteState]);

    React.useEffect(() => {
        if (!originSeriesPublicId) {
            return;
        }

        if (deferInitialLoad && (isDiscussionLoading || !discussionData)) {
            return;
        }

        if (seriesReadingContext?.seriesPublicId === originSeriesPublicId) {
            return;
        }

        setRouteState((previous) => ({
            ...previous,
            originSeriesPublicId: undefined,
        }), { replace: "canonicalize" });
    }, [deferInitialLoad, discussionData, isDiscussionLoading, originSeriesPublicId, seriesReadingContext?.seriesPublicId, setRouteState]);

    React.useEffect(() => {
        setCurrentAddedToSeriesCount(getVisibleDiscussionSeriesCount(addedToSeriesCount, initiallySavedToBookmarks));
        setIsAddedToAnySeries(initiallyAddedToAnySeries ?? false);
    }, [addedToSeriesCount, initiallyAddedToAnySeries, initiallySavedToBookmarks]);

    React.useEffect(() => {
        if (propHighlightedArgumentPublicId) {
            return;
        }

        const handleHashChange = () => {
            const nextHighlightedArgumentPublicId = getHighlightedArgumentPublicIdFromHash();
            setRouteState((previous) => (
                previous.highlightedArgumentPublicId === nextHighlightedArgumentPublicId
                    ? previous
                    : {
                        ...previous,
                        highlightedArgumentPublicId: nextHighlightedArgumentPublicId,
                    }
            // Mirroring a hash the BROWSER has already committed. The address changed before this ran,
            // so writing an entry here would add a second one for a navigation that already happened.
            ), { replace: "canonicalize" });
        };

        handleHashChange();
        window.addEventListener("hashchange", handleHashChange);
        return () => window.removeEventListener("hashchange", handleHashChange);
    }, [propHighlightedArgumentPublicId, setRouteState]);

    // One-tap counter-reply: "reply to your argument" notifications land on
    // /discussion/{d}/argument/{X}?reply=1. Read the flag (keyed on the focused argument so it also
    // catches soft-navigation between discussions), then strip it so a refresh/back does not re-open
    // the composer. The flag is forwarded to the highlighted argument's card, which opens its reply
    // composer once. See designs/04-live/2026-07-07-reply-notification-one-tap-respond-composer-deeplink-design.md
    const [autoOpenReply, setAutoOpenReply] = React.useState<boolean>(false);
    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const hasReplyFlag = params.get("reply") === "1";
        setAutoOpenReply(hasReplyFlag);
        if (!hasReplyFlag) {
            return;
        }
        params.delete("reply");
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
        replaceInPageHistoryUrl(nextUrl, "consume-one-shot");
    }, [openingArgumentPublicId]);

    // SV-130 Answer action: "asked for your answer" rows land on /discussion/{d}?write=1. Same one-shot
    // contract as `?reply=1` above — read, strip, act once — at the post level: the thoughts displayer
    // opens the top-level composer. Stripped so a refresh or Back does not re-open it.
    const [autoOpenWriteThought, setAutoOpenWriteThought] = React.useState<boolean>(false);
    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const hasWriteFlag = params.get("write") === "1";
        if (!hasWriteFlag) {
            return;
        }
        setAutoOpenWriteThought(true);
        params.delete("write");
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
        replaceInPageHistoryUrl(nextUrl, "consume-one-shot");
    }, [discussionPublicId]);

    // Hand-off reply surface (D5/A3, `designs/01-now/2026-09-06-empty-room-handoff-design.md` §7): the
    // hand-off card's CTA lands here with `?from=handoff`. Same one-shot contract as `?write=1` above:
    // read once, strip through the history helper, never reset (a deferred landing's data arrives on a
    // later run, when the parameter is already gone). The value reaches the top-level composers as
    // `publishSurface`, which puts `surface: "handoff"` on the publish request. A `from` that is not ours
    // is neither consumed nor forwarded.
    const [handoffPublishSurface, setHandoffPublishSurface] = React.useState<PublishThoughtSurface | undefined>(undefined);
    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        if (params.get(HANDOFF_FROM_PARAM) !== HANDOFF_FROM_VALUE) {
            return;
        }
        setHandoffPublishSurface("handoff");
        params.delete(HANDOFF_FROM_PARAM);
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
        replaceInPageHistoryUrl(nextUrl, "consume-one-shot");
    }, [discussionPublicId]);

    // Empty-room hand-off (ER-2a, `designs/01-now/2026-09-06-empty-room-handoff-design.md` §4): armed by
    // the share moment's PUBLISH kinds below, after the same author gate, on the same one-shot read of the
    // URL. Because the parameter is stripped before anything acts, a refresh arrives un-armed and the hook
    // never re-fetches (D14). `first_response` is an email CTA, not a publish, and never arms it.
    const [handoffArmed, setHandoffArmed] = React.useState(false);
    const handoffCandidate = useHandoffCandidate(handoffArmed, discussionPublicId);
    // Publish acknowledgement (first-session loop D3 / D12): armed by the SAME one-shot, in the same
    // branch as the hand-off, so there is exactly one copy of "this is the author's publish landing".
    const [publishedAckVisible, setPublishedAckVisible] = React.useState(false);

    // Share moment: `?sv_m=post_publish` (appended by the publish flow) or `?sv_m=first_response`
    // (carried by the FIRST_INTERACTION email CTA). Copies the `?reply=1` shape above deliberately —
    // read, strip, then act on the next tick.
    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const kind = readShareMomentKind(window.location.search);
        if (!kind) {
            return;
        }
        // Wait for the page data before consuming the one-shot (2026-09-14). The composer lands here
        // through EnhancedNavigation — a shell request, so the page mounts with `deferInitialLoad` and
        // `authorUsername` / `discussionPublicId` arrive only when `getDiscussionPageData` answers.
        // Stripping on this first, data-less run and returning at the gate below meant the moment never
        // opened on exactly the landing it was built for. The effect re-runs when the data lands (its
        // deps), and the parameter is still in the URL for that run. `discussionData` rather than the
        // author alone: a deleted author is a legitimate `undefined` that must still consume the flag.
        if (!discussionData) {
            return;
        }
        // Strip FIRST, and via replaceInPageHistoryUrl rather than history.replaceState — a hand-written
        // history entry carries neither `entryKind` nor `pageInstanceId`, which silently degrades Back to
        // a full navigation. `scripts/check-history-writers.mjs` fails the build on a direct call.
        replaceInPageHistoryUrl(
            withoutShareMomentParam(`${window.location.pathname}${window.location.search}${window.location.hash}`),
            "consume-one-shot",
        );

        // Viewer-gated: an email link forwarded to someone else, or a URL pasted before the strip ran,
        // renders nothing at all for the recipient. WHICH author the viewer must be depends on the kind
        // (round-26 A5) — see `THOUGHT_ADDRESSED_KINDS` below.
        const viewer = MetaHandler.getLoggedInUsername();
        if (!viewer) {
            return;
        }
        // A private post lands every recipient on an access wall — never worth prompting a share for.
        if (privateAccess || !discussionPublicId) {
            return;
        }

        // `answer_accepted` and `top_reply` congratulate the person who wrote the REPLY, who is almost
        // never the person who wrote the post — so the discussion-author gate is the wrong one for them,
        // and using it would have shown the post's author a moment about someone else's reply while
        // showing the replier nothing. The thought's id is the one in the landing URL
        // (`/discussion/{slug}/thought/{tid}`), and its author is resolved from the same endpoint the
        // focused card already uses rather than guessed from anything on this page.
        const isThoughtAddressed = kind === "answer_accepted" || kind === "top_reply";
        // The three pride moments always share the POST, never the deep-linked thought URL they arrived
        // on: `/og/discussion/{id}.png` is the only preview image that exists (round-25 D28), so a
        // `/thought/{tid}` target would carry a card for a page it is not.
        const shareUrlForKind = isThoughtAddressed || kind === "gaining_traction"
            ? `${window.location.origin}/discussion/${discussionSlug ?? discussionPublicId}`
            : `${window.location.origin}${window.location.pathname}`;

        const open = (onClosed?: () => void) => openShareMoment({
            kind,
            entityType: "DISCUSSION",
            entityId: discussionPublicId,
            // TOKEN-FREE: the chokepoint mints and appends `?sv_s` itself.
            url: shareUrlForKind,
            title: shareTitle,
            authorDisplayName: authorUsername,
            previewImageUrl: `${window.location.origin}/og/discussion/${discussionPublicId}.png`,
            localizer,
            onClosed,
        });

        if (isThoughtAddressed) {
            if (!openingArgumentPublicId) {
                return;
            }
            let cancelled = false;
            void (async () => {
                try {
                    const api = ClientFactory.getAjaxThoughtRestControllerApi();
                    const response = await api.thoughtOpening(openingArgumentPublicId);
                    const thoughtAuthor = response?.data?.openingArgument?.username;
                    if (cancelled || !thoughtAuthor || thoughtAuthor !== viewer) {
                        return;
                    }
                    open();
                } catch (error) {
                    // Losing a congratulation is the right degradation: the alternative is showing it to
                    // whoever happens to hold the link. Logged rather than swallowed so the moment's
                    // shown-count having no matching denominator is explainable.
                    logger.error("[DiscussionPage] could not resolve the thought's author for a share moment:", error);
                }
            })();
            return () => { cancelled = true; };
        }

        if (!authorUsername || viewer !== authorUsername) {
            return;
        }
        const isPublishLanding = kind === "post_publish" || kind === "first_post";
        if (isPublishLanding) {
            setHandoffArmed(true);
            setPublishedAckVisible(true);
        }
        // The author has just seen their post live: the Play review moment (review-prompt design D7).
        // Inert off the native shell. Queued BEHIND the share dialog, never over it; immediate only when
        // the dialog was suppressed. The bridge applies the server's session-age gate and delay.
        const askForReview = () => {
            void onReviewMoment(DISCUSSION_PUBLISHED_MOMENT);
        };

        // Defer one tick so the moment opens over an already-painted page rather than competing with the
        // mount, and so the stacked-dialog probe sees the final DOM. (The thought-addressed branch gets
        // this for free: it is already waiting on a network round trip.)
        const timer = window.setTimeout(() => {
            const shown = open(isPublishLanding ? askForReview : undefined);
            if (!shown && isPublishLanding) {
                askForReview();
            }
        }, 0);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discussionData, discussionPublicId, discussionSlug, openingArgumentPublicId, authorUsername, privateAccess, shareTitle]);

    /**
     * HOW MANY entries this instance pushed for the conversation full view. Zero means closing must not
     * pop: `/discussion/{slug}/conversation/{id}` is a shareable URL and a reply-notification target, so
     * a visitor can land *directly* on it — that entry is then their first in this document and popping
     * it would eject them from the site entirely. See the design's sections 11.5, 11.8 and 13.5.
     *
     * <p>A COUNT rather than the boolean this used to be. The boolean was sound only while the view
     * occupied exactly one entry, which is why moving between conversations inside the open view had to
     * replace — and under §13.1 that is a member action changing the address, so it must push. Counting
     * lets the view own as many entries as the member actually visited and still unwind to the one it
     * was opened over, in a single traversal.
     */
    const pushedConversationEntryCountRef = React.useRef(0);

    const isConversationViewOpen = currentSection === "conversation" && Boolean(selectedConversationPublicId);

    /**
     * Disarm whenever the view ends up closed by any route — most importantly the user pressing Back
     * themselves, which already closed it correctly, and the section tab bar (which renders alongside the
     * open view and legitimately pushes). Leaving the ref armed there would make a LATER close pop an
     * entry this instance never pushed.
     *
     * One-directional (rule 31): this writes a ref, never back into the route state it reads.
     */
    React.useEffect(() => {
        if (!isConversationViewOpen) {
            pushedConversationEntryCountRef.current = 0;
        }
    }, [isConversationViewOpen]);

    /**
     * Closing POPS the entry opening pushed, rather than pushing the conversations URL back on top.
     * Pushing left the stack holding two non-adjacent entries with the same URL, so Back visibly
     * RE-OPENED the conversation the user had just closed and leaving the discussion cost two presses.
     * The replace branch is the deep-link/arrival case above, where there is no entry of ours to pop.
     */
    const closeConversationView = React.useCallback(() => {
        const closingConversationId = openConversationIdRef.current;
        const pushedEntries = pushedConversationEntryCountRef.current;
        pushedConversationEntryCountRef.current = 0;
        // `popOwnInPageEntries` VERIFIES the entry on top still belongs to this page instance before it
        // moves the session (§13.5). Having pushed is not the same as still being on top — the
        // 2026-08-22 report was an overlay popping an entry a soft navigation had put above it — so a
        // refusal falls through to the same rewrite the arrival case uses.
        //
        // The pop's `popstate` re-reads the route state from the restored URL, so the pop branch
        // deliberately does not `setRouteState`: writing one too would fight the pop.
        if (pushedEntries < 1 || !popOwnInPageEntries(pushedEntries)) {
            setRouteState((previous) => ({
                ...previous,
                section: "conversations",
                conversationPublicId: undefined,
                highlightedArgumentPublicId: undefined,
            }), { replace: "canonicalize" });
        }
        focusConversationTrigger(closingConversationId);
    }, [focusConversationTrigger, setRouteState]);

    const handleConversationSelected = React.useCallback((nextConversationPublicId: string, nextArgumentPublicId?: string) => {
        if (!nextConversationPublicId) return;

        // Clicking the View control of the already-open conversation closes it (toggle), returning the
        // page to its thought list. It must go through the one close path, or half the defect survives
        // with the other half fixed.
        //
        // NOTE: unreachable as the page renders today — the thought list that owns the View control is
        // gated to the "conversations"/"opening" sections, so it is never on screen while this view is
        // open. Kept and routed correctly so that a layout which does put the two side by side inherits
        // the right behavior instead of re-introducing the push. See the design's section 11.8.
        const openConversationId = openConversationIdRef.current;
        if (openConversationId && openConversationId === nextConversationPublicId && !nextArgumentPublicId) {
            closeConversationView();
            return;
        }

        // Moving to another conversation WHILE the view is already open PUSHES, like the first open —
        // it is a member action that changes the address, so Back returns to the conversation they came
        // from (§13.1). It used to replace, so that the overlay owned exactly one entry and the boolean
        // ref above stayed sound; the count replaced the boolean precisely so this could push.
        //
        // (Unreachable as the page renders today: the thought list that owns the View control is gated
        // to the "conversations"/"opening" sections, so it is never on screen beside the open view. Kept
        // correct so a layout that does put the two together inherits the right behaviour.)
        //
        // Counted only while we own the entry the view OPENED on. Popping can reach a CLOSED view only
        // if there is an entry beneath ours where it was closed; a visitor who landed straight on
        // `/discussion/{slug}/conversation/{id}` owns no such entry, so their moves still push and
        // closing rewrites the address rather than popping onto the conversation they arrived in.
        if (!openConversationId || pushedConversationEntryCountRef.current >= 1) {
            pushedConversationEntryCountRef.current += 1;
        }

        setRouteState((previous) => ({
            ...previous,
            section: "conversation",
            conversationPublicId: nextConversationPublicId,
            highlightedArgumentPublicId: trimToUndefined(nextArgumentPublicId),
        }));
    }, [closeConversationView, setRouteState]);

    /**
     * The group filter and the thought ordering both PUSH (§13.1): each changes the address, so Back
     * returns to the previous filter or ordering. They replaced until 2026-08-22 on the "a filter
     * refines what you are already reading" reasoning §13.2 retired.
     *
     * The sanitizing effects above still replace, and that is not a disagreement between two writers of
     * one field — they rewrite an address the member never chose (a group id that is not on this
     * discussion), which is what `canonicalize` means.
     */
    const handleSelectedGroupPublicIdsChange = React.useCallback((nextSelectedGroupPublicIds: string[]) => {
        // `originGroupPublicId` is left alone on purpose: it is a fact about where the visit came from,
        // which changing the filter cannot invalidate. It used to be dropped here so the removed
        // auto-filter seed could not re-apply itself; there is no seed anymore.
        setRouteState((previous) => ({
            ...previous,
            selectedGroupPublicIds: normalizeDiscussionGroupPublicIds(nextSelectedGroupPublicIds),
        }));
    }, [setRouteState]);

    const handleOpeningArgumentOrderingChange = React.useCallback((nextOrdering: DiscussionOpeningThoughtOrderingStyle) => {
        setRouteState((previous) => ({
            ...previous,
            openingArgumentOrdering: nextOrdering,
        }));
    }, [setRouteState]);

    const shouldUseInitialConversationParticipants = selectedConversationPublicId === initialConversationPublicId;
    const shouldInferParticipantsForCurrentConversation = Boolean(
        selectedConversationPublicId && (
            !shouldUseInitialConversationParticipants || shouldInferConversationParticipantsFromArgumentOrder
        )
    );
    const canEdit = props.canEdit ?? Boolean(authorUsername && authorUsername === loggedInUsername);

    /**
     * `DISCUSSION-26` / `DISCUSSION-41` (wave-12 sheet Q4, option A) — the section list the mobile action
     * bar offers, and the handler a selection goes through.
     *
     * <p>Below `md` the section strip is a plain block that scrolls away with the article and never comes
     * back, while the fixed bar carries the post's ACTIONS and no navigation at all. This bag is what
     * lets the bar offer the sections without costing a pixel of vertical space, and what makes its count
     * button navigate to the section it counts instead of opening the composer.
     *
     * <p>Built from the SAME `buildDiscussionSectionDefs` the strip renders from, with the SAME
     * `buildSectionHref` and the SAME `setRouteState` handler, so the phone and the desktop cannot
     * disagree about which sections exist or where one leads. Nothing here writes history: selection goes
     * through the page's history-backed route state exactly as the strip's anchors do (rule 33).
     *
     * <p><b>Must stay ABOVE the `!discussionData` early return below</b>, for the reason spelled out on
     * `useSensitivePageUnlock` immediately after it: this component renders a skeleton first and
     * hydrates on a later pass, so a hook called after that return runs on the second render and not
     * the first. Every input here is resolved from props or route state before the data lands.
     */
    const mobileSectionNavigation = React.useMemo(() => ({
        sections: buildDiscussionSectionDefs({
            localizer: localizer!,
            section: currentSection,
            repliesTabLabel: resolveRepliesTabLabel(localizer!, isQuestion),
            discussionGroupCount: currentDiscussionGroups.length,
            discussionSeriesCount,
            canEdit,
            isDeleted,
            sectionCounts,
        }),
        activeSection: currentSection,
        buildHref: buildSectionHref,
        // `DISCUSSION-48` — the route state changes exactly as it always did, and THEN the reader is
        // taken to the panel they asked for. Only this handler scrolls; the strip's own anchors, Back
        // and soft navigation still do not (see `scrollSectionControlsIntoView`).
        onSelect: (section: string) => {
            setRouteState((previous) => ({ ...previous, section }));
            scrollSectionControlsIntoView();
        },
        // `DISCUSSION-62`, first half. The article is a destination on this page even though it is not a
        // "section", and it is the one destination the persistent chrome did not offer: this page runs
        // ~23,410 CSS px on a phone — about 28 viewports — as one unbounded scroll, so a reader deep in a
        // branching argument who wants to re-read the claim being argued about had to swipe up twenty
        // times. Only the bar survives that scroll.
        //
        // It scrolls rather than routing: the article is above every section panel on this page, so the
        // top of the document IS the post, and there is no `section` value (and so no URL) to change.
        // `behavior: "auto"` matches `scrollSectionControlsIntoView` above — no motion contract to honour.
        //
        // ⚠️ NOT taken, deliberately: whether a 110-thought thread should arrive on a phone as one
        // unbounded document or as a bounded first page a reader chooses to extend is that row's own
        // second, product half. This is only the cheap half the row itself separates out.
        onBackToArticle: () => {
            if (typeof window === "undefined") {
                return;
            }
            window.scrollTo({ top: 0, behavior: "auto" });
        },
        ariaLabel: localizer!("discussion.sections.nav.label"),
    }), [
        buildSectionHref,
        canEdit,
        currentDiscussionGroups.length,
        currentSection,
        discussionSeriesCount,
        isDeleted,
        isQuestion,
        localizer,
        scrollSectionControlsIntoView,
        sectionCounts,
        setRouteState,
    ]);

    // Must stay ABOVE the `!discussionData` early return below: this component renders a skeleton first and
    // hydrates deferred data on a later pass, so a hook called after that return runs on the second render
    // but not the first — "rendered more hooks than during the previous render", which throws away the whole
    // island and leaves the page with less than the server-rendered HTML already contained (AGENTS.md
    // rule 32). `isSensitive`/`isSpoiler` are resolved from props before data arrives, so this is also correct.
    const { revealed, reveal, sensitiveMasking } = useSensitivePageUnlock({
        kind: "discussion",
        id: discussionPublicId,
        isSensitive,
        isSpoiler,
    });

    if (!discussionData) {
        if (didDiscussionLoadFail && !isDiscussionLoading) {
            return (
                <DiscussionPageLoadFailed
                    localizer={localizer}
                    onRetry={() => setDiscussionLoadAttempt((previous) => previous + 1)}
                />
            );
        }

        return <DiscussionPageSkeleton localizer={localizer} section={currentSection} />;
    }

    const resolvedDiscussionPublicId = discussionPublicId ?? "";
    const resolvedDiscussionSlug = discussionSlug ?? "";

    // The conversation always renders as a full view in the main column with its own Back button.
    const renderConversationDisplayer = () => (
        <ConversationDisplayer
            key={`conversation:${selectedConversationPublicId}`}
            localizer={localizer}
            conversationPublicId={selectedConversationPublicId ?? ""}
            openingUsername={shouldUseInitialConversationParticipants ? normalizedOpeningUsername : undefined}
            reactingUsername={shouldUseInitialConversationParticipants ? normalizedReactingUsername : undefined}
            shouldInferParticipantsFromArgumentOrder={shouldInferParticipantsForCurrentConversation}
            discussionPublicId={resolvedDiscussionPublicId}
            agreementAgreementTitle={agreementAgreementTitle}
            disagreementAgreementTitle={disagreementAgreementTitle}
            loggedInUsername={loggedInUsername}
            isUserLoggedIn={isUserLoggedIn}
            isSensitive={isSensitive}
            suppressSpoilerGuards={isSpoiler}
            highlightedArgumentPublicId={highlightedConversationArgumentPublicId}
            onBackToConversations={closeConversationView}
        />
    );
    const isReadOnlyDiscussion = isArchived || isDeleted;
    const hasResolvedDiscussionGroups = discussionGroupsSourcePublicId === discussionPublicId && hasLoadedDiscussionGroups;
    const viewerBelongsToAnyDiscussionGroup = currentDiscussionGroups.some((group) => Boolean(group.isUserPartOfGroup || group.isUserLeaderOfGroup));
    const viewerCanParticipateInPrivateDiscussion = !privateAccess || canEdit || viewerBelongsToAnyDiscussionGroup;
    const shouldShowPrivateAccessHelperCopy = hasResolvedDiscussionGroups && !viewerBelongsToAnyDiscussionGroup;
    const shouldShowDiscussionParticipationNotice = (renderedSection === "conversations" || renderedSection === "opening") && !isArchived && !isDeleted;

    const openAddDiscussionToGroupsDialog = () => {
        if (!resolvedDiscussionPublicId) {
            return;
        }

        showAddDiscussionToGroupsDialog(resolvedDiscussionPublicId, localizer, {
            isPrivateDiscussion: privateAccess,
            isDiscussionAuthor: canEdit,
        });
    };

    const showGroupsSection = () => {
        setRouteState((previous) => ({
            ...previous,
            section: "groups",
        }));
    };

    const discussionVisibilityBanner = !privateAccess || isReadOnlyDiscussion || !shouldShowPrivateAccessHelperCopy ? null : (() => {
        const shouldShowGroupsAction = currentDiscussionGroups.length > 0 && currentSection !== "groups";
        const shouldShowAddToGroupAction = resolvedDiscussionPublicId.length > 0 && currentDiscussionGroups.length === 0;

        return (
            <div data-playwright-id="discussion-visibility-banner">
                <InfoBanner
                    icon={Lock}
                        title={<span data-playwright-id="discussion-visibility-title">{localize(localizer, "discussion.visibility.private")}</span>}
                    description={(
                        <div className="space-y-3" data-playwright-id="discussion-visibility-content">
                            <p data-playwright-id="discussion-visibility-description">
                                {localize(
                                    localizer,
                                currentDiscussionGroups.length > 0
                                    ? "discussion.private.banner.with_groups"
                                    : "discussion.private.banner.no_groups",
                            )}
                            </p>
                            {shouldShowGroupsAction || shouldShowAddToGroupAction ? (
                                <div className="flex flex-wrap gap-2">
                                    {shouldShowGroupsAction ? (
                                        <Button type="button" variant="secondary" size="sm" onClick={showGroupsSection} data-playwright-id="discussion-visibility-view-groups">
                                        {localize(localizer, "discussion.private.banner.view_groups")}
                                        </Button>
                                    ) : null}
                                    {shouldShowAddToGroupAction ? (
                                        <Button type="button" variant="secondary" size="sm" onClick={openAddDiscussionToGroupsDialog} data-playwright-id="discussion-visibility-add-to-group">
                                        {localizer("typescript.discussion.add_to_group")}
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    )}
                />
            </div>
        );
    })();

    const discussionParticipationNotice = !shouldShowDiscussionParticipationNotice || !privateAccess || !hasResolvedDiscussionGroups ? null : (() => {
        if (currentDiscussionGroups.length === 0) {
            return (
                <div data-playwright-id="discussion-participation-notice">
                    <InfoBanner
                        icon={Lock}
                        title={<span data-playwright-id="discussion-participation-title">{localize(localizer, "discussion.participation.notice.no_groups.title")}</span>}
                        description={(
                            <div className="space-y-3" data-playwright-id="discussion-participation-content">
                                <p data-playwright-id="discussion-participation-description">
                                {localize(
                                    localizer,
                                    canEdit
                                        ? "discussion.participation.notice.no_groups.author"
                                        : "discussion.participation.notice.no_groups.viewer",
                                )}
                                </p>
                                {resolvedDiscussionPublicId ? (
                                    <Button type="button" variant="secondary" size="sm" onClick={openAddDiscussionToGroupsDialog} data-playwright-id="discussion-participation-add-to-group">
                                    {localizer("typescript.discussion.add_to_group")}
                                    </Button>
                                ) : null}
                            </div>
                        )}
                    />
                </div>
            );
        }

        if (viewerCanParticipateInPrivateDiscussion) {
            return null;
        }

        return (
            <div data-playwright-id="discussion-participation-notice">
                <InfoBanner
                    icon={Lock}
                    title={<span data-playwright-id="discussion-participation-title">{localize(localizer, "discussion.participation.notice.restricted.title")}</span>}
                    description={(
                        <div className="space-y-3" data-playwright-id="discussion-participation-content">
                            <p data-playwright-id="discussion-participation-description">{localize(localizer, "discussion.participation.notice.restricted.description")}</p>
                            <Button type="button" variant="secondary" size="sm" onClick={showGroupsSection} data-playwright-id="discussion-participation-view-groups">
                            {localize(localizer, "discussion.private.banner.view_groups")}
                            </Button>
                        </div>
                    )}
                />
            </div>
        );
    })();

    const discussionContentDisplayerProps: any = {
        localizer,
        discussionPublicId: resolvedDiscussionPublicId,
        discussionSlug: resolvedDiscussionSlug,
        tagNames: props.tagNames,
        showPrivateAccessHelperCopy: shouldShowPrivateAccessHelperCopy,
        authorUsername,
        authorProfileImageUrl: discussionData?.authorProfileImageUrl,
        createdAt,
        updatedAt,
        editedAfterReplyAt,
        authorFeaturedDistinction: discussionData?.authorFeaturedDistinction,
        authorVoiceOfMonthGroupName: discussionData?.authorVoiceOfMonthGroupName,
        bodyTextLength,
        agreementAgreementTitle,
        disagreementAgreementTitle,
        condensed,
        title,
        privateAccess,
        sensitive: isSensitive,
        spoiler: isSpoiler,
        isArchived,
        archivedAt,
        isDeleted,
        deletedAt,
        deletedByModerator,
        deletedReasonType,
        deletedMessage,
        agreementsCount,
        disagreementsCount,
        argumentsCount,
        addedToSeriesCount: currentAddedToSeriesCount,
        initiallyAgreeing,
        initiallyDisagreeing,
        initiallyAddedToAnySeries: isAddedToAnySeries,
        initiallySavedToBookmarks,
        canEdit,
        contentHtml,
        contentLanguageTag,
        seriesReadingContext,
        discussionVisibilityBanner,
        discussionGroupsSummary: discussionContextSummary,
        isQuestion,
        mindChanged: hasMindChanged,
        questionState,
        onQuestionStateChanged: handleQuestionStateChanged,
        askToAnswerAvailable: discussionData?.askToAnswerAvailable === true,
        passageBlockIds: discussionData?.passageBlockIds,
        passageAnchoredThoughts,
        // Read from the live page data first, falling back to the mount props — the same order every
        // other field here uses, so a soft navigation shows the destination post's review and not the
        // one the reader arrived from.
        //
        // The author's own edit wins over both, and that ordering is the whole reason the override
        // exists: after a successful save the server's copy is stale until the next load, so showing
        // the author the value they just removed would read as the save having failed.
        reviewSubjectName: reviewFacetOverride
            ? reviewFacetOverride.subjectName
            : discussionData?.reviewSubjectName ?? props.reviewSubjectName,
        // A newly-attached subject has no slug until the page reloads, so the header renders its name
        // as plain text rather than as a link to nowhere. ReviewHeader already handles a missing slug.
        reviewSubjectSlug: reviewFacetOverride
            ? undefined
            : discussionData?.reviewSubjectSlug ?? props.reviewSubjectSlug,
        reviewRatingHalfStars: reviewFacetOverride
            ? reviewFacetOverride.ratingHalfStars
            : discussionData?.reviewRatingHalfStars ?? props.reviewRatingHalfStars,
        reviewSubjectPublicId: discussionData?.reviewSubjectPublicId ?? props.reviewSubjectPublicId,
        onReviewFacetChanged: handleReviewFacetChanged,
        // SV-141 D1: a short post's page has no section controls, so the phone bar gets no copy of them.
        mobileSectionNavigation: isShortPostPage ? undefined : mobileSectionNavigation,
        // SV-141 D1: the page's own gate, handed down so the card drops Version history and the bar
        // withholds every thought-composer door on exactly the posts this page treats as short.
        isShortPostPage,
        // Round-33 `2b`: the name the card hides inside a short post's <h1>, from whichever transport
        // delivered this post (the mount props on a direct load, /page-data on a soft navigation).
        pageName: discussionData?.pageName,
        /* The BOARD facet, rendered INSIDE the post card above the body — the position
           `discussion.jte:414-422` already server-renders it in (master design §4.1d, §18.1).

           Until 2026-08-13 this was a sibling BELOW `DiscussionContentDisplayer`, so the SSR grid and
           the island grid occupied different boxes: every board post reflowed on hydration and the
           board read as a second, unrelated card. Passing it as a slot is what makes the two paths
           agree; see the 2026-08-13 tier grid redesign §6.

           NOT a second MOUNT_MAP entry: this container is the page's ONE react-component, so a nested
           mount would be deleted by createRoot() (master §11.1, corrected 2026-08-07; rule 32).
           lazyWithRetry gives the code-split boundary and the retry-and-reload without a mount
           boundary.

           Rendered only when the server actually sent a board. `boardView` is undefined both for a
           non-board post and for a board whose server-side load FAILED — and in the second case the
           render is already noindex and the server rendered no grid to subtract from, so drawing
           nothing here is correct rather than lossy.

           The boundary is a `LazyControlBoundary` (FI-070, 2026-09-15; it was a bare `MountErrorBoundary`
           with no `componentName` until then). Same scope — a crash or a chunk that never arrives costs
           the grid, not the discussion around it — but the fallback is what the member already had:
           the server's own grid, sliced from this island's snapshot, exactly as the pending state
           below, under its own `-ssr-restored` hook so a recovery never reads as merely pending. The
           boundary logs the failure and marks the document (`data-lazy-control-failed`), which is
           what `islandHealthGuard.mjs` reads. Null on a client-owned arrival with no snapshot, which
           is what a failed server-side board load renders too. */
        // BOARD-21 (D17): a COMPLETE board follows the body, on the island exactly as in `discussion.jte`.
        // Read from the server-sent view, not BoardGrid's live state, so a board completed in-session keeps
        // its position until the next load rather than jumping under the member's finger.
        boardAfterBody: boardView?.complete === true,
        boardSlot: boardView ? (
            <LazyControlBoundary
                controlName="board-grid"
                fallback={<SsrSlotFallback componentName="DiscussionPage" selector={BOARD_SSR_SELECTOR} playwrightId="discussion-board-ssr-restored" />}
            >
                {/* FI-046: while the chunk is pending the box holds the server's own grid, not nothing
                    (rule 32). Sliced from this island's snapshot; null on a client-owned arrival. */}
                <React.Suspense fallback={<SsrSlotFallback componentName="DiscussionPage" selector={BOARD_SSR_SELECTOR} playwrightId="discussion-board-ssr-pending" />}>
                    <BoardGrid
                        localizer={localizer}
                        discussionPublicId={discussionPublicId ?? ""}
                        board={boardView}
                        canParticipate={isUserLoggedIn}
                    />
                </React.Suspense>
            </LazyControlBoundary>
        ) : boardPayloadMissing ? (
            /* FS-7 / FI-319: rule 32's fourth door, the one a lazy SLOT owns. The controller writes the
               REAL grid into `discussion.jte` first and serializes this island's JSON companion after,
               swallowing a failure with a `log.warn` — so the only thing that can be missing here is the
               payload, never the markup. `createRoot()` then discards that markup, and the box used to
               render as `null`: the page ends with LESS than the HTML the server shipped, which is the
               one thing rule 32 forbids. The server's own grid goes back, inert, on the same
               `SsrSlotFallback` machinery the chunk-failure and render-failure doors already use.

               The marker is not optional. A restored grid looks EXACTLY like a healthy one, so no
               screenshot and no content assertion can tell them apart; without it this recovery path
               would silently widen the set of broken states the Playwright suite reports as green.
               `data-island-board-payload-failed` has its probe row in
               `typescript/apps/playwright/tests/helpers/islandHealthGuard.mjs` and is pinned from this
               side by `ssrRestoreMarkerContract.spec.ts`. The VALUE names the door. */
            <div data-island-board-payload-failed="inline-json">
                <SsrSlotFallback
                    componentName="DiscussionPage"
                    selector={BOARD_SSR_SELECTOR}
                    playwrightId="discussion-board-payload-ssr-restored"
                />
            </div>
        ) : null,
        /* The POLL facet, on the board's exact terms above: a slot, not a sibling, so the island and
           `poll_block.jte` occupy the same box; the same containment boundary, so a crash or a missing
           chunk costs the block and not the discussion; rendered only when the server sent a poll. */
        pollSlot: pollView ? (
            <LazyControlBoundary
                controlName="poll-block"
                fallback={<SsrSlotFallback componentName="DiscussionPage" selector={POLL_SSR_SELECTOR} playwrightId="discussion-poll-ssr-restored" />}
            >
                <React.Suspense fallback={<SsrSlotFallback componentName="DiscussionPage" selector={POLL_SSR_SELECTOR} playwrightId="discussion-poll-ssr-pending" />}>
                    <PollBlock
                        localizer={localizer}
                        discussionPublicId={discussionPublicId ?? ""}
                        poll={pollView}
                        canParticipate={isUserLoggedIn}
                    />
                </React.Suspense>
            </LazyControlBoundary>
        ) : pollPayloadMissing ? (
            /* FS-7 / FI-319, the poll's copy of the board's degraded door above, on the same terms and
               for the same reason. Its own marker, not the board's: a shared marker cannot tell the
               guard WHICH facet degraded, and these two fail independently. */
            <div data-island-poll-payload-failed="inline-json">
                <SsrSlotFallback
                    componentName="DiscussionPage"
                    selector={POLL_SSR_SELECTOR}
                    playwrightId="discussion-poll-payload-ssr-restored"
                />
            </div>
        ) : null,
    };
    return (
        <SensitivePageGate
            isSensitive={isSensitive}
            isSpoiler={isSpoiler}
            hideSensitiveMedia={MetaHandler.getHideSensitiveMedia()}
            revealed={revealed}
            onReveal={reveal}
            localizer={localizer}
            badgeLabel={pageWarningCopy.badgeLabel}
            title={pageWarningCopy.title}
            helperText={pageWarningCopy.helperText}
            revealText={pageWarningCopy.revealText}
            showEnablePreferenceButton={pageWarningCopy.showSensitivePreferenceAction}
            revealButtonDataPlaywrightId="discussion-page-sensitive-reveal"
        >
            <div className="w-full bg-background pb-6">
              {/* NO sm-UP HORIZONTAL PADDING ON THE FOUR max-w-250 WRAPPERS BELOW, DELIBERATELY
                  (`DISCUSSION-27`). From `sm` up the shell gutters this column: `basic_layout.jte`'s
                  `mainClass` puts `px-4 sm:px-4 lg:px-6` on `<main>`, and the body card adds its own
                  `p-4 sm:p-5` inside. Below `sm` this page opts into the shell's phone edge variant
                  (SV-138, `phoneEdgeToEdge`), so `<main>` pays nothing there: the body card goes flat and
                  keeps its `p-4`, and the three other wrappers carry `max-sm:px-4` instead. Re-applying `px-4` here made the phone pay the same gutter three
                  times — 48 px a side on a 412 px viewport, a 313 px measure, and 8 of the opening
                  paragraph's 28 lines breaking mid-word. The four siblings move together or the column's
                  left edge breaks, and `discussion/discussion.jte`'s SSR twins must match them or the
                  island's mount shifts the article sideways. Pinned by
                  `discussionPageGutters.contract.test.tsx`. */}
              <div className="mx-auto w-full max-w-7xl px-0 lg:px-6">
                <div className="grid w-full gap-2 lg:grid-cols-[minmax(0,1fr)_28rem]">
                <main className="min-w-0">
                <div className="mx-auto mb-5 w-full max-w-250 pb-2">
                    {/* D3: the one-line publish acknowledgement, at the top of the author's post page,
                        once, after the redirect. INSIDE this wrapper on purpose: a fifth `max-w-250`
                        sibling would change the count `discussionPageGutters.contract.test.tsx` pins. */}
                    {publishedAckVisible ? (
                        <PublishedAcknowledgement localizer={localizer} onClose={() => setPublishedAckVisible(false)} />
                    ) : null}

                    {/* The BOARD facet reaches this card through its `boardSlot` prop, so it renders
                        inside the card above the body — the same box the server render puts it in. */}
                    {/* Short post only (round 31 `2c`): the post it responds to, above the body. Nothing
                        server-rendered lives in this box, so a pending chunk, a crash or an empty
                        answer all render nothing (rule 32: this only adds). */}
                    {isShortPostPage && discussionPublicId ? (
                        <LazyControlBoundary controlName="short-post-focal-parent" fallback={null}>
                            <React.Suspense fallback={null}>
                                <FocalPostParentLine rootPublicId={discussionPublicId} localizer={localizer} />
                            </React.Suspense>
                        </LazyControlBoundary>
                    ) : null}

                    <DiscussionContentDisplayer {...discussionContentDisplayerProps} />

                    {/* ...and its replies below it, the focal navigator (`?focal=`, rule 33). Inside this
                        wrapper, not a fifth `max-w-250` sibling (`discussionPageGutters.contract.test.tsx`). */}
                    {isShortPostPage && discussionPublicId ? (
                        <LazyControlBoundary controlName="short-post-focal" fallback={null}>
                            <React.Suspense fallback={null}>
                                <FocalPostNavigator rootPublicId={discussionPublicId} localizer={localizer} signedIn={isUserLoggedIn} />
                            </React.Suspense>
                        </LazyControlBoundary>
                    ) : null}
                </div>

                {/* Follow-author prompt. Below the body on purpose: it is a recommendation element, not
                    a control on the author byline — rule 23 bullet 6, and the shape reverted on
                    2026-08-30. It renders nothing at all unless a trigger has fired and the viewer is
                    signed in, is not the author, and does not already follow them. */}
                <div className="mx-auto w-full max-w-250 max-sm:px-4">
                    <FollowAuthorPrompt
                        authorUsername={authorUsername ?? ""}
                        discussionPublicId={discussionPublicId ?? ""}
                        loggedInUsername={loggedInUsername}
                        initiallyFollowingAuthor={initiallyFollowingAuthor}
                        authorFollowableByPrompt={authorFollowableByPrompt}
                        engagementTriggered={followPromptEngagementTriggered}
                        localizer={localizer}
                    />
                </div>

                {/* SV-141 D1: a short post's page has no sections to switch between. */}
                {isShortPostPage ? null : (
                    <div className="mx-auto mb-5 flex w-full max-w-250 max-sm:px-4" ref={sectionControlsRef} data-playwright-id="discussion-section-controls-row">
                        <DiscussionPageSectionControls
                            localizer={localizer}
                            section={currentSection}
                            onSectionChanged={(section: string) => setRouteState((previous) => ({
                                ...previous,
                                section,
                            }))}
                            buildSectionHref={buildSectionHref}
                            discussionGroupCount={currentDiscussionGroups.length}
                            discussionSeriesCount={discussionSeriesCount}
                            sectionCounts={sectionCounts}
                            canEdit={canEdit}
                            isDeleted={isDeleted}
                            isQuestion={isQuestion}
                        />
                    </div>
                )}

                <div className="mx-auto flex w-full min-w-0 max-w-250 flex-col items-stretch gap-5 max-sm:px-4">
                    {renderedSection === "conversation" && selectedConversationPublicId
                        ? renderConversationDisplayer()
                        : null}

                    {renderedSection === "conversations" || renderedSection === "opening" ? (
                        <>
                            {/* In-content ad at the section boundary between the opening content +
                                controls (above) and the argument list (below). Renders null
                                (reserving nothing) when ads are disabled or consent is not granted,
                                and never sits inside an argument card. */}
                            {/* SV-141 D7: not on a short post's page, where it would sit inside a thread. */}
                            {isShortPostPage ? null : <AdSlot placement="discussion-incontent" localizer={localizer} />}
                            {discussionParticipationNotice ? (
                                <div className="mb-4">
                                    {discussionParticipationNotice}
                                </div>
                            ) : null}
                            {/* Empty-room hand-off: the one post by somebody else the author is offered
                                right after publishing. Above the (usually empty) replies list, where the
                                author's eye lands; additive, so a 204 or a failure leaves this slot
                                exactly as it was. */}
                            {/* SV-141 D7: not on a short post's page. */}
                            {handoffCandidate && renderedSection === "conversations" && !isShortPostPage ? (
                                <HandoffCard candidate={handoffCandidate} localizer={localizer} />
                            ) : null}
                            {/* Reply invitation (T1): a welcoming, DESCRIPTIVE norm line above the reply
                                list, shown on every discussion since 2026-09-12 (SV-46 ship-to-100). Matias (PNAS 2019) measured +70% newcomer participation from
                                exactly this across 2,190 r/science discussions — but the same literature
                                found that emphasising MODERATION norms increases toxicity, so this line must
                                never become a rule or a warning. Gated to the "conversations" section: on
                                "opening" the list below is a single focused thread, not the discussion's
                                replies. See designs/01-now/2026-07-28-reply-invitation-norm-line-and-zero-
                                reply-composer-design.md. */}
                            {/* SV-141 D1: a short post invites no thoughts. Round 42 A5 (`DISCUSSION-112`):
                                nor does a post whose author is a deleted user, because "authors here usually
                                reply" promises a reply that cannot come (T5). Deleted = the loaded page data
                                carries no author name, the same signal the byline prints "Deleted user" for
                                (`discussion.jte` stamps `data-author-username=""`). Hidden, not re-worded. */}
                            {renderedSection === "conversations" && !isShortPostPage && !isAuthorDeleted ? (
                                // A57 (`DISCUSSION-86`): a statement, not a container. Unboxed, so the
                                // composer is the only bordered box in the band and the first box a thumb
                                // reaches is one that takes typing. Rendering only: words and gate unchanged.
                                <p
                                    className="px-1 text-sm text-muted-foreground"
                                    data-playwright-id="reply-invitation-norm-line"
                                >
                                    {localizer("replyInvitation.normLine")}
                                </p>
                            ) : null}
                            {/* FI-546 (round-35 B1, SV-141 D1): a short post takes no thoughts (the server
                                refuses them, ThoughtService.java:2174-2187); its replies are the thread. */}
                            {isShortPostPage ? null : (
                            <DiscussionThoughtsDisplayer
                                localizer={localizer}
                                // Seed only — the requested ordering is still fetched. See the prop's
                                // docs for why this is not `items`.
                                initialItems={props.inlinedThoughts}
                                initialItemsOrdering={props.inlinedThoughtsOrdering}
                                discussionPublicId={resolvedDiscussionPublicId}
                                discussionCreatedAt={createdAt}
                                initialViewerDiscussionAgreement={initiallyDisagreeing ? "DISAGREE" : "AGREE"}
                                loggedInUsername={loggedInUsername}
                                isUserLoggedIn={isUserLoggedIn}
                                openingArgumentPublicId={openingArgumentPublicId}
                                agreementAgreementTitle={agreementAgreementTitle}
                                disagreementAgreementTitle={disagreementAgreementTitle}
                                discussionSlug={resolvedDiscussionSlug}
                                // Share payload only (BL-083): a thought's sheet title is its HOST
                                // post's title, and the last component that holds one is this page.
                                discussionTitle={shareTitle}
                                isSensitive={isSensitive}
                                suppressSpoilerGuards={isSpoiler}
                                isArchived={isArchived}
                                isDeleted={isDeleted}
                                discussionGroupsByPublicId={discussionGroupsByPublicId}
                                selectedGroupPublicIds={selectedGroupPublicIds}
                                onSelectedGroupPublicIdsChange={handleSelectedGroupPublicIdsChange}
                                openingArgumentOrdering={openingArgumentOrdering}
                                // The list's own filter + ordering row renders inside the displayer,
                                // between the composer and the list it acts on (2026-07-30). It used to
                                // sit up in the section tab row above.
                                onOpeningArgumentOrderingChange={handleOpeningArgumentOrderingChange}
                                onItemsChanged={handleThoughtItemsChanged}
                                discussionGroups={currentDiscussionGroups}
                                hasAnyDiscussionGroups={currentDiscussionGroups.length > 0}
                                isGroupsLoading={isDiscussionGroupsLoading}
                                onEnsureDiscussionGroupsLoaded={ensureAllDiscussionGroupsLoaded}
                                onViewConversationClicked={(nextConversationPublicId, argumentPublicId) => handleConversationSelected(nextConversationPublicId, argumentPublicId)}
                                autoOpenReply={autoOpenReply}
                                autoOpenWriteThought={autoOpenWriteThought}
                                publishSurface={handoffPublishSurface}
                                isQuestion={isQuestion}
                                onQuestionStateChanged={handleQuestionStateChanged}
                                // CHANGED MY MIND: the badge names the POST author, because only they
                                // can mark. Sourced here because this is the component that holds the
                                // post payload; deriving it from the marked thought would name the
                                // recipient of the credit instead of the person whose mind moved.
                                discussionAuthorUsername={authorUsername}
                                viewerLastVisitedAt={latchedViewerLastVisitedAt}
                            />
                            )}
                        </>
                    ) : null}

                    {renderedSection === "participants" && discussionPublicId ? (
                        <ParticipantsListDisplayer
                            key={`participants:${resolvedDiscussionPublicId}:${participantsRefreshKey}`}
                            localizer={localizer!}
                            discussionPublicId={resolvedDiscussionPublicId}
                            sensitiveMasking={sensitiveMasking}
                            conversationsHref={buildSectionHref("conversations")}
                            onOpenConversations={() => setRouteState((previous) => ({ ...previous, section: "conversations" }))}
                        />
                    ) : null}

                    {renderedSection === "quoted-by" && discussionPublicId ? (
                        <QuotedByDiscussionsListDisplayer
                            localizer={localizer!}
                            discussionPublicId={resolvedDiscussionPublicId}
                            sensitiveMasking={sensitiveMasking}
                            isDeleted={isDeleted}
                        />
                    ) : null}

                    {renderedSection === "related" && discussionPublicId ? (
                        <RelatedDiscussionsListDisplayer
                            localizer={localizer!}
                            discussionPublicId={resolvedDiscussionPublicId}
                            sensitiveMasking={sensitiveMasking}
                            isDeleted={isDeleted}
                        />
                    ) : null}

                    {renderedSection === "groups" && discussionPublicId ? (
                        <GroupsListDisplayer
                            localizer={localizer!}
                            discussionPublicId={resolvedDiscussionPublicId}
                            isPrivateDiscussion={privateAccess}
                            isDiscussionAuthor={canEdit}
                            isDeleted={isDeleted}
                            sensitiveMasking={sensitiveMasking}
                            prependGroups={prependGroups}
                            removedGroupIds={removedGroupIds}
                        />
                    ) : null}

                    {renderedSection === "series" && discussionPublicId ? (
                        <SeriesListDisplayer
                            localizer={localizer!}
                            discussionPublicId={resolvedDiscussionPublicId}
                            isDeleted={isDeleted}
                            sensitiveMasking={sensitiveMasking}
                            prependSeries={prependSeries}
                            removedSeriesIds={removedSeriesIds}
                        />
                    ) : null}

                    {renderedSection === "requests" && discussionPublicId && canEdit && !isDeleted ? (
                        <DiscussionRequestsList
                            localizer={localizer!}
                            discussionPublicId={resolvedDiscussionPublicId}
                            removedRequestIds={removedDiscussionRequestIds}
                        />
                    ) : null}

                </div>
                </main>
                  <DiscussionRailScrollRegion>
                    <DiscussionRail
                        discussionPublicId={resolvedDiscussionPublicId || undefined}
                        localizer={localizer!}
                        seedGroups={currentDiscussionGroups}
                        disabled={isDeleted}
                        // `DISCUSSION-38`: the rail yields the module this section already serves. Passed
                        // as the LIVE `renderedSection`, not `props.section` — a soft tab change and a Back
                        // both move the panel without remounting the island, and a mount-captured value
                        // would leave the rail duplicating whichever section the reader arrived on.
                        activeSection={renderedSection}
                    />
                  </DiscussionRailScrollRegion>
                </div>
              </div>
            </div>
        </SensitivePageGate>
    );
}

/**
 * The rail's viewport-capped scroll box, plus the one cue saying it continues (`DISCUSSION-8`).
 *
 * <p>The cap and the internal scroll are a recorded decision
 * (`designs/04-live/2026-07-06-discussion-rail-sticky-spans-comments-design.md`: "cap it to the
 * visible height and let it scroll internally instead of clipping") and this component does not
 * reopen it. What that design never specified is any affordance telling the reader the region
 * scrolls, and `no-scrollbar` — which is product-wide — removes the only one the browser would have
 * drawn. Measured 2026-08-13: ~2,932px of rail content in a 1,136px box at a 1200px viewport, so the
 * two cards that actually ask the reader to do something ("In these groups", "Part of a series")
 * were never seen on a normal laptop.
 *
 * <p>The cue is a bottom fade, shown only while there is more below and gone at the end of the
 * scroll, so it means "there is more" rather than decorating the edge permanently. It is
 * `aria-hidden` and `pointer-events-none`: it adds a signal and takes nothing away.
 *
 * <p>The gradient is written here rather than as a token because it has to fade to the colour the
 * rail actually sits on (`--background`); `--content-fade-gradient` fades to `--card` and belongs to
 * the condensed article.
 */
export function DiscussionRailScrollRegion(props: { children: React.ReactNode }) {
    const scrollRef = React.useRef<HTMLElement | null>(null);
    const [hasMoreBelow, setHasMoreBelow] = React.useState(false);
    const [isScrollable, setIsScrollable] = React.useState(false);

    React.useEffect(() => {
        const node = scrollRef.current;
        if (!node) {
            return;
        }

        // 2px of slack: a fractional scroll height at a non-integer device pixel ratio otherwise
        // leaves the cue on forever at the bottom of the list, which is the opposite of the signal.
        //
        // TWO flags, deliberately, and they must not be collapsed into one. `hasMoreBelow` is about
        // the CUE and goes false at the bottom of the scroll, which is exactly right for a fade
        // meaning "there is more". `isScrollable` is about FOCUSABILITY and must stay true all the
        // way down, or a keyboard user scrolling to the end would have the tab stop pulled out from
        // under them mid-interaction.
        const measure = () => {
            setHasMoreBelow(node.scrollHeight - node.clientHeight - node.scrollTop > 2);
            setIsScrollable(node.scrollHeight - node.clientHeight > 2);
        };
        measure();

        node.addEventListener("scroll", measure, { passive: true });

        let observer: ResizeObserver | undefined;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(measure);
            observer.observe(node);
        }

        return () => {
            node.removeEventListener("scroll", measure);
            observer?.disconnect();
        };
    }, []);

    return (
        <aside
            ref={scrollRef}
            className="min-w-0 lg:sticky lg:top-[calc(4rem_+_var(--seavien-connection-banner-height,0px))] lg:self-start lg:max-h-[calc(100vh_-_4rem_-_var(--seavien-connection-banner-height,0px))] lg:overflow-y-auto no-scrollbar"
            // `DISCUSSION-8`, the other half. An `overflow-y-auto` box that no keyboard can reach is a
            // WCAG 2.1.1 failure, and it is the whole failure here: this is already an `<aside>`, so it
            // already exposes the `complementary` landmark. Adding `role="region"` would REPLACE that
            // landmark with a weaker one and would then owe an accessible name — which is what got
            // this deferred on a catalog key it never needed. Focusability is the entire fix.
            //
            // Conditional, because the box only overflows at `lg` and only with enough rail content;
            // an unconditional tab stop would be a dead stop on mobile and on a short rail.
            tabIndex={isScrollable ? 0 : undefined}
            data-rail-has-more-below={hasMoreBelow ? "true" : "false"}
            data-rail-scrollable={isScrollable ? "true" : "false"}
        >
            {props.children}
            {hasMoreBelow ? (
                <div
                    className="pointer-events-none sticky bottom-0 -mt-16 hidden h-16 lg:block"
                    style={{
                        background: "linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--background) 60%, transparent) 55%, var(--background) 100%)",
                    }}
                    data-playwright-id="discussion-rail-scroll-cue"
                    aria-hidden={true}
                />
            ) : null}
        </aside>
    );
}

/* Small paginated list displayers that call existing AJAX endpoints and reuse card displayers */

function ParticipantsListDisplayer(props: { discussionPublicId: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    localizer: Localizer;
    /** The conversations section's real address, for the empty state's onward anchor (`DISCUSSION-102`). */
    conversationsHref?: string;
    /** Switches the page to conversations through its history-backed route state (rule 33). */
    onOpenConversations?: () => void }) {
    const {discussionPublicId, localizer, sensitiveMasking, conversationsHref, onOpenConversations} = props;

    const fetchPage = React.useCallback(async (cutoffCreationDate?: string) => {
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        // Participants are the users who argued in the discussion; votes are private.
        const resp = await api.usersParticipatingInDiscussion(discussionPublicId, cutoffCreationDate ?? undefined);
        return Array.isArray(resp.data) ? resp.data : [];
    }, [discussionPublicId]);

    const getId = React.useCallback((u: UserProfileDTO) => u?.username ?? undefined, []);

    return (
        <div className="w-full flex justify-center">
            <PaginatedListWrapperV2<UserProfileDTO, string>
                key={`participants:${discussionPublicId}`}
                fetchPage={fetchPage}
                getId={getId}
                // `DISCUSSION-35`: without `pageSize` the wrapper's `inferHasMoreFromFirstPage`
                // cannot settle exhaustion locally, so a complete 9-of-9 roster still advertised a
                // next page and ended in a spinner reading "Loading…" — at the exact moment the
                // reader had finished it — plus one empty round trip per visit. 20 is the literal
                // passed to the repository by `PaginationService.usersParticipatingInDiscussion`.
                // The bound is deliberately one-sided: an exactly-full page still costs one empty
                // fetch, which is the cheaper error than hiding real rows.
                pageSize={20}
                className={MAIN_PAGE_LIST_WIDTH_CLASS}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <ProfileCardListSkeleton />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("home.end-of-list"),
                    errorMessage: localizer("paginated.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    empty: (
                        /**
                         * `DISCUSSION-102` (r41 A45). This was the shared "Nothing to show here yet.",
                         * which on a people list reads like a load failure and offered no next step
                         * while its Related sibling offers one. A roster is empty exactly when nobody
                         * has replied, so the reply invitation's sentence is TRUE here and costs no
                         * catalog bytes, and the onward path is the section where a reply is written.
                         *
                         * A real anchor to the section's own address (middle-click opens it); a plain
                         * click goes through the page's route state exactly as the section strip's
                         * anchors do, so nothing here writes history (rule 33).
                         */
                        <PaginatedListEmptyState
                            icon={Users}
                            title={localizer("discussion.section.participants")}
                            description={localizer("replyInvitation.zeroReplyPlaceholder")}
                            action={conversationsHref ? (
                                <Button asChild size="sm" className="rounded-full">
                                    <a
                                        href={conversationsHref}
                                        data-playwright-id="discussion-participants-empty-onward"
                                        onClick={(event) => {
                                            if (isBrowserOwnedClick(event) || !onOpenConversations) {
                                                return;
                                            }
                                            event.preventDefault();
                                            onOpenConversations();
                                        }}
                                    >
                                        <MessagesSquare className="size-4" aria-hidden={true} />
                                        {localizer("discussion.section.conversations")}
                                    </a>
                                </Button>
                            ) : undefined}
                        />
                    )
                })}
                render={({items}) => (
                    <MainPageListStack>
                        {items.map(u => (
                            <UserCardDisplayer
                                key={u.username}
                                user={u}
                                localizer={localizer}
                                viewerIsLeaderOfGroup={false}
                                surface="discussion_participants"
                                sensitiveMasking={sensitiveMasking}
                            />
                        ))}
                    </MainPageListStack>
                )}
            />
        </div>
    );
}

/**
 * `DISCUSSION-35`, the related half — and the one mount on this page whose page size is NOT a server
 * literal to be read.
 *
 * <p>`PaginatedListWrapperV2` settles exhaustion locally only when the mount supplies a `pageSize`,
 * and without one a short first page still advertises a next page: the sentinel fires and the reader
 * is shown "Loading…" for a round trip that returns nothing. The partial closure of this row adopted
 * the literal at the participants and groups mounts and deliberately left this one alone, because
 * `pageSize` is a CLAIM ABOUT THE SERVER and a wrong-high guess HIDES REAL ROWS — strictly worse than
 * the spinner.
 *
 * <p><b>What reading the server settled.</b> There is no literal here.
 * `DiscussionRelationshipFacade.findRelatedDiscussions` normalizes a CLIENT-supplied page size
 * against `seavien.discovery.relationships.default-page-size` (6) and `.max-page-size` (12), neither
 * of which is overridden in any profile. So the fix is not to guess the server's number but to
 * SUPPLY one: the same constant goes to the endpoint and to the wrapper, and the claim holds by
 * construction rather than by a comment promising two numbers will stay equal.
 *
 * <p>6 rather than 12 on purpose. It is the property's own default, so nothing about today's
 * responses changes; and it is the value that stays honoured if `max-page-size` is ever lowered,
 * whereas a client asking for the maximum would start silently under-reading the moment it moved.
 */
const RELATED_SECTION_PAGE_SIZE = 6;

function RelatedDiscussionsListDisplayer(props: {
    discussionPublicId: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    localizer: Localizer;
    isDeleted?: boolean;
}) {
    const { discussionPublicId, localizer, sensitiveMasking, isDeleted = false } = props;

    const fetchPage = React.useCallback(async (paginationParameter?: string | number) => {
        // A deleted discussion's sections are suppressed server-side (discussion.jte gates the
        // groups/series/tags blocks on !isDeleted) and every one of these endpoints answers 400
        // "Deleted posts are no longer available for this action". Firing them buys nothing but
        // origin load and FRONTEND error noise. See the D3 section of
        // designs/01-now/2026-08-01-draft-body-integrity-tombstone-islands-and-schedule-validation-design.md
        if (isDeleted) {
            return [];
        }
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        const normalizedCutoffPosition = paginationParameter == null
            ? undefined
            : typeof paginationParameter === "number"
                ? paginationParameter
                : Number.parseInt(paginationParameter, 10);
        const response = await api.relatedDiscussions(
            discussionPublicId,
            undefined,
            Number.isFinite(normalizedCutoffPosition) ? normalizedCutoffPosition : undefined,
            RELATED_SECTION_PAGE_SIZE,
        );
        return response.data ?? [];
    }, [discussionPublicId, isDeleted]);

    const getId = React.useCallback((discussion: RelatedDiscussionDTO) => discussion?.publicId ?? undefined, []);

    return (
        <div className="flex w-full justify-center" data-playwright-id="discussion-related-list">
            <PaginatedListWrapperV2<RelatedDiscussionDTO, string | number>
                key={`related:${discussionPublicId}`}
                // Back-navigation restoration (SV-24), page-scoped. Mirrors `key=` and adds `isDeleted`,
                // which is load-bearing here rather than cosmetic: a deleted post short-circuits fetchPage
                // to [], so sharing one key across both states could restore a non-empty list onto a
                // tombstone. This is the only one of the four discussion sections that is restored -- the
                // other three reflect a viewer action (agreeing, adding to a group, saving to a series)
                // through parent state that resets on remount, so a restore would undo it on screen.
                restorationKey={`discussion:related:${discussionPublicId}:${isDeleted ? "deleted" : "live"}`}
                fetchPage={fetchPage}
                getId={getId}
                // `DISCUSSION-35`'s remaining half. See RELATED_SECTION_PAGE_SIZE: this is the SAME
                // constant the fetch sends, which is what makes the claim true rather than guessed.
                pageSize={RELATED_SECTION_PAGE_SIZE}
                className={MAIN_PAGE_LIST_WIDTH_CLASS}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <DiscussionCardListSkeleton />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("home.end-of-list"),
                    errorMessage: localizer("paginated.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    empty: (
                        /**
                         * `DISCUSSION-11`. This panel used to render a title and a description that
                         * restated it, with neither of the `action` / `secondary` slots its two
                         * siblings on this page already pass — and on mobile there is no rail, so this
                         * box was the entire answer on the one route whose whole job is discovery.
                         *
                         * The onward path is the home feed's active-ordered view, which is the same
                         * corpus the rail's own fallback module serves. Both strings are the ones
                         * `DISCUSSION-1` wrote FOR that corpus, so the honesty constraint it settled is
                         * inherited rather than re-argued: the panel names what it is offering and does
                         * not call it "related". Reusing them also costs no catalog bytes, which the
                         * locale bundle budget no longer has.
                         *
                         * A real anchor rather than an `onClick`, so middle-click and open-in-new-tab
                         * behave; `?feed=all` is the home page's own parameter for that view.
                         */
                        <PaginatedListEmptyState
                            icon={Share2}
                            title={localizer("discussion.related.empty.title")}
                            // `DISCUSSION-116` (r41 C13): its own description. The rail's subtitle ("Busy
                            // conversations right now.") read as a contradiction of the title, or as a
                            // service-busy error, and never said the button leads somewhere else.
                            description={localizer("discussion.related.empty.description")}
                            action={(
                                <Button asChild size="sm" className="rounded-full">
                                    <a
                                        href="/?feed=all"
                                        data-playwright-id="discussion-related-empty-onward"
                                    >
                                        <Compass className="size-4" aria-hidden={true} />
                                        {localizer("discussion.rail.active.title")}
                                    </a>
                                </Button>
                            )}
                        />
                    ),
                })}
                render={({ items }) => (
                    <MainPageListStack>
                        {items.map((discussion) => (
                            <DiscussionCardDisplayer
                                key={discussion.publicId}
                                discussion={discussion}
                                localizer={localizer}
                                sensitiveMasking={sensitiveMasking}
                            />
                        ))}
                    </MainPageListStack>
                )}
            />
        </div>
    );
}

function GroupsListDisplayer(props: {
    discussionPublicId: string;
    localizer: Localizer;
    isPrivateDiscussion?: boolean;
    isDiscussionAuthor?: boolean;
    isDeleted?: boolean;
    sensitiveMasking?: SensitiveMaskingDirective;
    prependGroups?: GroupDTO[];
    removedGroupIds?: Set<string>;
}) {
    const {
        discussionPublicId,
        localizer,
        isPrivateDiscussion = false,
        isDiscussionAuthor = false,
        isDeleted = false,
        sensitiveMasking,
        prependGroups = [],
        removedGroupIds = new Set(),
    } = props;
    const fetchPage = React.useCallback(async (cutoffAddedAt?: string) => {
        // A deleted discussion's sections are suppressed server-side (discussion.jte gates the
        // groups/series/tags blocks on !isDeleted) and every one of these endpoints answers 400
        // "Deleted posts are no longer available for this action". Firing them buys nothing but
        // origin load and FRONTEND error noise. See the D3 section of
        // designs/01-now/2026-08-01-draft-body-integrity-tombstone-islands-and-schedule-validation-design.md
        if (isDeleted) {
            return [] as GroupDTO[];
        }
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        const resp = await api.groupsForDiscussion(discussionPublicId, cutoffAddedAt ?? undefined);
        return Array.isArray(resp.data) ? resp.data : [];
    }, [discussionPublicId, isDeleted]);

    const getId = React.useCallback((g: GroupDTO) => g?.publicId ?? undefined, []);
    const emptyStateTitle = isPrivateDiscussion
        ? localize(localizer, "discussion.groups.empty.private.title")
        : localizer("discussion.groups.empty.title");
    const emptyStateDescription = isPrivateDiscussion
        ? localize(localizer, "discussion.groups.empty.private.description")
        : localizer("discussion.groups.empty.description");

    return (
        <div className="w-full flex justify-center">
            <PaginatedListWrapperV2<GroupDTO, string>
                key={`groups:${discussionPublicId}`}
                fetchPage={fetchPage}
                getId={getId}
                // `DISCUSSION-35`, the same defect and the same fix as the participants list above.
                // 20 is the literal passed to the repository by
                // `PaginationService.groupsForDiscussion`.
                //
                // The page's other two `PaginatedListWrapperV2` mounts (related, series) are
                // deliberately left WITHOUT a `pageSize`: `pageSize` is a claim about the server, and
                // theirs could not be read — series resolves through `GroupSeriesPaginationService`,
                // and related takes a CLIENT-supplied page size this page leaves undefined. A
                // wrong-high guess hides real rows, so omitting it is the safe half.
                pageSize={20}
                className={MAIN_PAGE_LIST_WIDTH_CLASS}
                getVisibleItemsCount={(items) => mergeDiscussionGroups(items, prependGroups, removedGroupIds).length}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <ProfileCardListSkeleton />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("home.end-of-list"),
                    errorMessage: localizer("paginated.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    empty: (
                        <PaginatedListEmptyState
                            icon={Share2}
                            title={emptyStateTitle}
                            description={emptyStateDescription}
                            action={!isDeleted ? {
                                icon: Share2,
                                label: localizer("typescript.discussion.add_to_group"),
                                onClick: () => showAddDiscussionToGroupsDialog(discussionPublicId, localizer, {
                                    isPrivateDiscussion,
                                    isDiscussionAuthor,
                                })
                            } : undefined}
                        />
                    )
                })}
                render={({items}) => {
                    const combined = mergeDiscussionGroups(items, prependGroups, removedGroupIds);
                    return (
                        <MainPageListStack>
                            {combined.map((g, idx) => (
                                <GroupCardDisplayer key={getId(g) ?? `group-${idx}`} group={g} sensitiveMasking={sensitiveMasking} surface="discussion_related" localizer={localizer} />
                            ))}
                        </MainPageListStack>
                    );
                }}
            />
        </div>
    );
}

function SeriesListDisplayer(props: {
    discussionPublicId: string;
    localizer: Localizer;
    isDeleted?: boolean;
    sensitiveMasking?: SensitiveMaskingDirective;
    prependSeries?: SeriesDTO[];
    removedSeriesIds?: Set<string>;
}) {
    const {discussionPublicId, localizer, isDeleted = false, sensitiveMasking, prependSeries = [], removedSeriesIds = new Set()} = props;
    const fetchPage = React.useCallback(async (cutoffAddedAt?: string) => {
        // A deleted discussion's sections are suppressed server-side (discussion.jte gates the
        // groups/series/tags blocks on !isDeleted) and every one of these endpoints answers 400
        // "Deleted posts are no longer available for this action". Firing them buys nothing but
        // origin load and FRONTEND error noise. See the D3 section of
        // designs/01-now/2026-08-01-draft-body-integrity-tombstone-islands-and-schedule-validation-design.md
        if (isDeleted) {
            return [] as SeriesDTO[];
        }
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        const resp = await api.seriesForDiscussion(discussionPublicId, cutoffAddedAt ?? undefined);
        return Array.isArray(resp.data) ? resp.data : [];
    }, [discussionPublicId, isDeleted]);

    const getId = React.useCallback((s: SeriesDTO) => s?.publicId ?? undefined, []);

    return (
        <div className="w-full flex justify-center">
            <PaginatedListWrapperV2<SeriesDTO, string>
                key={`series:${discussionPublicId}`}
                fetchPage={fetchPage}
                getId={getId}
                // `DISCUSSION-35`, the series half. 20 is the literal
                // `GroupSeriesPaginationService.seriesForDiscussion` passes to
                // `findSeriesForDiscussionOrderedByAddedAt` — read from the server rather than
                // guessed, which is the whole condition the partial closure set for adopting it.
                pageSize={20}
                className={MAIN_PAGE_LIST_WIDTH_CLASS}
                getVisibleItemsCount={(items) => {
                    const visiblePrependedSeries = filterBookmarksSeries(prependSeries);
                    const filteredItems = filterBookmarksSeries(items).filter(i => !removedSeriesIds.has(i.publicId!));
                    return [
                        ...visiblePrependedSeries,
                        ...filteredItems.filter(i => !visiblePrependedSeries.some(p => p.publicId === i.publicId))
                    ].length;
                }}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <SeriesCardListSkeleton />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("home.end-of-list"),
                    errorMessage: localizer("paginated.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    empty: (
                        <PaginatedListEmptyState
                            icon={ListPlus}
                            title={localizer("discussion.series.empty.title")}
                            description={localizer("discussion.series.empty.description")}
                            action={!isDeleted ? {
                                icon: ListPlus,
                                label: localizer("typescript.discussion.add_to_series"),
                                onClick: () => showAddDiscussionToSeriesDialog(discussionPublicId, localizer)
                            } : undefined}
                        />
                    )
                })}
                render={({items}) => {
                    const visiblePrependedSeries = filterBookmarksSeries(prependSeries);
                    const filteredItems = filterBookmarksSeries(items).filter(i => !removedSeriesIds.has(i.publicId!));
                    const combined = [
                        ...visiblePrependedSeries,
                        ...filteredItems.filter(i => !visiblePrependedSeries.some(p => p.publicId === i.publicId))
                    ];
                    return (
                        <MainPageListStack>
                            {combined.map((s, idx) => (
                                <SeriesCardDisplayer key={getId(s) ?? `series-${idx}`} series={s} sensitiveMasking={sensitiveMasking} localizer={localizer} />
                            ))}
                        </MainPageListStack>
                    );
                }}
            />
        </div>
    );
}

/**
 * ⚠️ Must equal the page size the request sends AND the server's own default
 * (`AjaxDiscussionRestController.QUOTED_BY_PAGE_SIZE`). `PaginatedListWrapperV2` only settles
 * exhaustion locally when the mount supplies one, and a wrong-HIGH guess HIDES real rows
 * (`DISCUSSION-35`). Three copies of one number, and the two that can disagree silently are this one
 * and the server's.
 */
const QUOTED_BY_SECTION_PAGE_SIZE = 6;

/**
 * "Quoted by": the posts that quoted this one (quote post, design §10.3).
 *
 * <p>The half that makes the relation visible from the other end, and the half that makes the count
 * real. Without it the notification tells the quoted author something happened and gives them nowhere
 * to see it — which is exactly how `reply_requests` and the newsletter became unmeasurable.
 *
 * <p>Modelled on {@code RelatedDiscussionsListDisplayer} below, with one deliberate difference: the
 * cursor is the quote EDGE id rather than a ranking position, so the list is ordered by when each quote
 * happened rather than by when each quoting post was written.
 */
function QuotedByDiscussionsListDisplayer(props: {
    discussionPublicId: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    localizer: Localizer;
    isDeleted?: boolean;
}) {
    const { discussionPublicId, localizer, sensitiveMasking, isDeleted = false } = props;

    const fetchPage = React.useCallback(async (paginationParameter?: string | number) => {
        // Same short circuit and the same reason as the related list: a deleted post's sections are
        // suppressed server-side, so firing this buys nothing but origin load and frontend error noise.
        if (isDeleted) {
            return [];
        }
        const cutoffQuoteId = paginationParameter == null
            ? undefined
            : typeof paginationParameter === "number"
                ? paginationParameter
                : Number.parseInt(paginationParameter, 10);
        return ClientFactory.fetchQuotedByDiscussions(
            discussionPublicId,
            Number.isFinite(cutoffQuoteId) ? (cutoffQuoteId as number) : undefined,
            QUOTED_BY_SECTION_PAGE_SIZE,
        );
    }, [discussionPublicId, isDeleted]);

    const getId = React.useCallback((discussion: DiscussionWithoutContentDTO) => discussion?.publicId ?? undefined, []);

    return (
        <div className="flex w-full justify-center" data-playwright-id="discussion-quoted-by-list">
            <PaginatedListWrapperV2<DiscussionWithoutContentDTO, string | number>
                key={`quoted-by:${discussionPublicId}`}
                restorationKey={`discussion:quoted-by:${discussionPublicId}:${isDeleted ? "deleted" : "live"}`}
                fetchPage={fetchPage}
                getId={getId}
                pageSize={QUOTED_BY_SECTION_PAGE_SIZE}
                className={MAIN_PAGE_LIST_WIDTH_CLASS}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <DiscussionCardListSkeleton />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("home.end-of-list"),
                    errorMessage: localizer("paginated.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    // Reached only in the narrow window where the server-side count said "some" and the
                    // list says "none" — a quote that was demoted or deleted between the two reads. The
                    // section is hidden outright at a count of zero, so this is not the ordinary empty
                    // state and it deliberately offers no onward path: there is nothing to send the
                    // reader to, and inventing one would be a fabricated destination.
                    empty: (
                        <PaginatedListEmptyState
                            icon={Quote}
                            title={localizer("discussion.quoted_by.empty.title")}
                        />
                    ),
                })}
                render={({ items }) => (
                    <MainPageListStack>
                        {items.map((discussion) => (
                            <DiscussionCardDisplayer
                                key={discussion.publicId}
                                discussion={discussion}
                                localizer={localizer}
                                sensitiveMasking={sensitiveMasking}
                            />
                        ))}
                    </MainPageListStack>
                )}
            />
        </div>
    );
}


/**
 * Reads the thoughts the server inlined next to the crawlable list it rendered.
 *
 * <p>Must run in `getProps`, i.e. BEFORE `mountReactNode` — the payload lives inside the mount
 * container, and `createRoot()` discards the container's children.
 *
 * Every failure mode returns `undefined`, which means "fetch as before". Absent element (deferred
 * shell, or a degraded server render that deliberately omitted it), unparseable JSON, or a non-array
 * payload must never be able to turn into an empty list — that would tell the island the discussion
 * has no thoughts, which is the one outcome worse than fetching.
 */
/**
 * The ordering the server says its inlined rows are in.
 *
 * <p>Returns undefined when the payload element is absent or carries no declaration, which callers
 * read as "assume the mirrored default" — the value that was true before this was read at all. That
 * keeps the two deploy halves order-independent: a frontend newer than the templates sees no
 * attribute and behaves exactly as it did, and a template newer than the frontend emits an attribute
 * nobody reads.
 *
 * <p>⚠️ Normalized, not trusted. `normalizeDiscussionOpeningThoughtOrdering` maps anything it does not
 * recognise to the client default (`best`), which for THIS caller would be the wrong fallback — it
 * would strip the cursors of a seed that is in fact in the default ordering. So an unrecognised value
 * returns undefined and takes the mirrored default instead.
 */
function parseInlinedThoughtsOrdering(element: HTMLElement): DiscussionOpeningThoughtOrderingStyle | undefined {
    const declared = element.querySelector("script[data-discussion-thoughts]")
        ?.getAttribute("data-thoughts-ordering")
        ?.trim();
    if (!declared) {
        return undefined;
    }
    if (!isRecognizedDiscussionOpeningThoughtOrdering(declared)) {
        logger.warn(
            `[DiscussionPage] mount: the server declared an unrecognised thoughts ordering `
            + `("${declared}"); falling back to the mirrored default rather than believing it as "best"`,
        );
        return undefined;
    }
    return normalizeDiscussionOpeningThoughtOrdering(declared);
}

function parseInlinedThoughts(element: HTMLElement): OpeningThoughtWithGroupMatchesDTO[] | undefined {
    const payloadEl = element.querySelector("script[data-discussion-thoughts]");
    if (!payloadEl) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(payloadEl.textContent ?? "");
        return Array.isArray(parsed) ? (parsed as OpeningThoughtWithGroupMatchesDTO[]) : undefined;
    } catch (error) {
        logger.warn("[DiscussionPage] mount: inlined thoughts payload was not valid JSON; falling back to fetching", error);
        return undefined;
    }
}

export const mountDiscussionPage = createReactMount<DiscussionPageProps>({
    name: "mountDiscussionPage",
    getProps: ({ element, dataset, parsed, localizer }) => {
        const contentEl = element.querySelector("[data-content]") as HTMLElement | null;
        const isDeleted = dataset.deleted === "true";
        const deferInitialLoad = dataset.deferInitialLoad === "true";

        if (!contentEl && !isDeleted && !deferInitialLoad) {
            logger.warn("[DiscussionPage] mount: content element ([data-content]) not found; rendering empty contentHtml");
        }

        // BOARD facet. The flag is emitted outside every `@if` in `discussion.jte`, so it is always
        // present and always truthful; the board's CONTENTS are not, travelling as a separate inlined
        // payload the template writes only when its server-side load succeeded. That makes
        // `boardView === undefined` ambiguous on its own — "not a board post" and "a board post whose
        // load failed" are the same value — and this bit is what separates them.
        const isBoard = dataset.isBoard === "true";
        const boardView = parseInlinedBoardView(element);
        // POLL, the same two reads as the board's, for the same reasons.
        const isPoll = dataset.isPoll === "true";
        const pollView = parseInlinedPollView(element);
        if (isPoll && !pollView && !deferInitialLoad && !isDeleted) {
            logger.warn(
                "[DiscussionPage] mount: the server marked this post as a poll but inlined no poll payload; "
                + "the block will not render (degraded poll load)",
            );
        }

        // The one combination that should not occur. Nothing renders differently for it yet (whether
        // the island should FETCH the board here is a UX decision, deliberately not taken — see the
        // board plan's §H3), but a degraded board load used to be indistinguishable from an ordinary
        // post, so it could not be measured at all. Suppressed on the deferred soft-navigation render,
        // where the payload is absent by construction rather than by failure.
        if (isBoard && !boardView && !deferInitialLoad && !isDeleted) {
            logger.warn(
                "[DiscussionPage] mount: the server marked this post as a board but inlined no board payload; "
                + "the grid will not render (degraded board load)",
            );
        }

        return {
            localizer,
            isBoard,
            section: dataset.section,
            /*
             * Comma-joined by `discussion.jte`; safe because TagService.CANONICAL_TAG_PATTERN admits
             * only [a-z0-9_]. An absent attribute (an older server render) and an empty one both mean
             * "no topics", and both produce [] rather than a row of blanks.
             */
            tagNames: (dataset.tagNames ?? "").split(",").map((name) => name.trim()).filter(Boolean),
            discussionPublicId: dataset.discussionPublicId?.trim() || undefined,
            discussionSlug: dataset.discussionSlug?.trim() || undefined,
            authorUsername: normalizeUsernameToUndefined(dataset.authorUsername),
            authorProfileImageUrl: dataset.authorProfileImageUrl?.trim() || undefined,
            createdAt: dataset.createdAt?.trim() || undefined,
            updatedAt: dataset.updatedAt,
            // An absent attribute (an older server render) and an empty one both mean "not edited
            // after a reply", and both must produce undefined rather than an empty-string timestamp.
            editedAfterReplyAt: dataset.editedAfterReplyAt?.trim() || undefined,
            // Distinctions (`2a`): an empty attribute means "no badge", exactly like an absent one.
            authorFeaturedDistinction: dataset.authorFeaturedDistinction?.trim() || undefined,
            authorVoiceOfMonthGroupName: dataset.authorVoiceOfMonthGroupName?.trim() || undefined,
            // Reading time (`2a`): absent attribute reads as "unknown", which renders no label.
            bodyTextLength: numberOrUndefined(dataset.bodyTextLength),
            agreementAgreementTitle: dataset.agreementAgreementTitle?.trim() || undefined,
            disagreementAgreementTitle: dataset.disagreementAgreementTitle?.trim() || undefined,
            condensed: dataset.condensed === "true",
            title: dataset.title ?? "",
            // Round-33 `2b`: the server's page name for a short post's visually hidden <h1> (`data-page-name`).
            pageName: dataset.pageName?.trim() || undefined,
            privateAccess: dataset.privateAccess === "true",
            sensitive: dataset.sensitive === "true",
            spoiler: dataset.spoiler === "true",
            isArchived: dataset.archived === "true",
            archivedAt: dataset.archivedAt,
            isDeleted,
            deletedAt: dataset.deletedAt?.trim() || undefined,
            deletedByModerator: dataset.deletedByModerator === "true",
            deletedReasonType: dataset.deletedReasonType?.trim() || undefined,
            deletedMessage: dataset.deletedMessage?.trim() || undefined,
            agreementsCount: dataset.agreementsCount ? Number(dataset.agreementsCount) : 0,
            disagreementsCount: dataset.disagreementsCount ? Number(dataset.disagreementsCount) : 0,
            addedToSeriesCount: dataset.addedToSeriesCount ? Number(dataset.addedToSeriesCount) : 0,
            // Counts carried on the non-deferred (full-load) SSR path, which never fetches
            // /page-data. Without these the opening-post comment count (argumentsCount) and the
            // comments ("conversations") badge default to 0 / undefined → wrong number on direct
            // loads, reloads, and browser-back (only in-app soft-nav fetches page-data).
            argumentsCount: numberOrUndefined(dataset.argumentsCount),
            conversationsCount: numberOrUndefined(dataset.conversationsCount),
            // `DISCUSSION-9`: participants was the one countable tab whose badge depended on how the
            // reader ARRIVED — computed server-side all along, but carried only on /page-data, so the
            // cold load an anonymous visitor from search or a shared link always takes showed a blank
            // beside three badges. The comment above records this same bug being fixed once for the
            // conversations badge; participants was not carried along.
            participantsCount: numberOrUndefined(dataset.participantsCount),
            quotedByCount: numberOrUndefined(dataset.quotedByCount),
            initiallyAgreeing: (dataset.initiallyAgreeing ?? dataset.initiallyAgreement) === "true",
            initiallyDisagreeing: (dataset.initiallyDisagreeing ?? dataset.initiallyDisagreement) === "true",
            initiallyAddedToAnySeries: dataset.initiallyAddedToAnySeries === "true",
            initiallySavedToBookmarks: dataset.initiallySavedToBookmarks === "true",
            initiallyFollowingAuthor: dataset.initiallyFollowingAuthor === "true",
            // `=== "true"` and not `!== "false"`: an attribute the server did not emit must read as NOT
            // followable. See the prop's doc comment.
            authorFollowableByPrompt: dataset.authorFollowableByPrompt === "true",
            // Round 37 `2a`: empty string (anonymous, first visit) reads as absent.
            viewerLastVisitedAt: dataset.viewerLastVisitedAt?.trim() || undefined,
            canEdit: dataset.canEdit === "true",
            contentHtml: isDeleted ? "" : (contentEl?.innerHTML ?? ""),
            contentLanguageTag: dataset.languageTag?.trim() || undefined,
            conversationPublicId: dataset.conversationPublicId,
            openingUserPresent: dataset.openingUserPresent === "true",
            reactingUserPresent: dataset.reactingUserPresent === "true",
            openingUsername: normalizeUsernameToUndefined(dataset.openingUsername),
            reactingUsername: normalizeUsernameToUndefined(dataset.reactingUsername),
            openingArgumentPublicId: dataset.openingThoughtPublicId,
            highlightedArgumentPublicId: dataset.highlightedThoughtPublicId?.trim() || undefined,
            deferInitialLoad,
            seriesReadingContext: normalizeSeriesReadingContext(parsed.seriesReadingContext),
            inlinedThoughts: parseInlinedThoughts(element),
            inlinedThoughtsOrdering: parseInlinedThoughtsOrdering(element),
            // Read here, in getProps, for the same reason the thoughts payload is: it lives inside the
            // mount container and createRoot() discards the container's children.
            boardView,
            isPoll,
            pollView,
            isQuestion: dataset.isQuestion === "true",
            // CHANGED MY MIND: one bit, read the same way the question bit is. Defaults to false when
            // the attribute is absent, so the two deploy halves are order-independent and the failure
            // mode is a missing badge rather than a wrong one.
            mindChanged: dataset.mindChanged === "true",
            // PASSAGE anchors (BL-160): `normalizeProps` JSON-parses the attribute into an array. Anything
            // that is not an array (an older server render, a malformed value) reads as "not carried",
            // which keeps the client fetch rather than rendering no anchors on a post that has them.
            passageBlockIds: Array.isArray(parsed.passageBlockIds)
                ? (parsed.passageBlockIds as unknown[]).map((id) => (typeof id === "string" ? id : null))
                : undefined,
            questionState: parseQuestionState(dataset.questionState),
            acceptedThoughtPublicId: dataset.acceptedThoughtPublicId?.trim() || undefined,
            // SV-130: `=== "true"`, so an attribute the server did not emit withholds the item.
            askToAnswerAvailable: dataset.askToAnswerAvailable === "true",
            reviewSubjectName: dataset.reviewSubjectName?.trim() || undefined,
            reviewSubjectSlug: dataset.reviewSubjectSlug?.trim() || undefined,
            reviewRatingHalfStars: numberOrUndefined(dataset.reviewRatingHalfStars),
            reviewSubjectPublicId: dataset.reviewSubjectPublicId?.trim() || undefined,
        };
    },
    render: (props) => <DiscussionPage {...props} />,
});

export default DiscussionPage;
